import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { nanoid } from "nanoid";
import { 
  QuestionType, 
  DateTimeConfig, 
  DEFAULT_DATE_TIME_CONFIG, 
  HeatMapConfig, 
  DEFAULT_HEAT_MAP_CONFIG, 
  HeatMapRegion,
  DEFAULT_PICK_GROUP_RANK_CONFIG,
  PickGroupRankItem,
  PickGroupRankGroup,
  SkipLogicRule,
  DisplayCondition,
  OptionLogic,
  OptionItem,
  SegmentationRule,
  GridRow,
  GridColumn,
} from "@/types/survey";
import { LogicBuilder } from "./LogicBuilder";
import { OptionLogicEditor } from "./OptionLogicEditor";
import { DisplayConditionBuilder } from "./DisplayConditionBuilder";
import { SegmentationRuleEditor } from "./SegmentationRuleEditor";
import { API_URL, getAuthToken } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  GripVertical, 
  Edit, 
  Trash2, 
  Copy, 
  Plus, 
  X,
  Video,
  Smartphone,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Upload,
  Download,
  ImagePlus,
  Target,
  MousePointerClick,
  Square,
  Hexagon,
  Eye,
  EyeOff,
  Check,
  GitBranch,
  Settings2,
  Users,
} from "lucide-react";

interface QuestionCardProps {
  question: QuestionType;
  index: number;
  onChange: (question: QuestionType) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  isDragging: boolean;
  dragHandleProps?: Record<string, unknown>;
  surveyId?: number; // For image uploads
  allQuestions?: QuestionType[]; // For "copy regions between questions" (Phase 4)
}

export function QuestionCard({ 
  question, 
  index, 
  onChange, 
  onDelete, 
  onDuplicate, 
  isDragging,
  dragHandleProps,
  allQuestions,
  surveyId
}: QuestionCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingImageIndex, setUploadingImageIndex] = useState<number | null>(null);
  const [signedImageUrls, setSignedImageUrls] = useState<Record<string, string>>({});
  const [isUploadingHeatMapImage, setIsUploadingHeatMapImage] = useState(false);
  
  // ============================================
  // Heat Map Region Drawing State (Phase 2 + Phase 4)
  // ============================================
  type RegionDrawMode = "idle" | "drawing" | "resizing" | "moving" | "drawing_polygon" | "editing_vertex";
  type ResizeHandle = "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w";
  type RegionShapeMode = "rect" | "poly";  // Phase 4: Toggle between rectangle and polygon
  
  const [regionDrawMode, setRegionDrawMode] = useState<RegionDrawMode>("idle");
  const [activeRegionId, setActiveRegionId] = useState<string | null>(null);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null);
  const [drawStartPoint, setDrawStartPoint] = useState<{ xPct: number; yPct: number } | null>(null);
  const [editingRegionName, setEditingRegionName] = useState<string | null>(null);
  const regionImageRef = useRef<HTMLDivElement>(null);
  
  // Phase 4: Polygon-specific state
  const [regionShapeMode, setRegionShapeMode] = useState<RegionShapeMode>("rect");
  const [polygonVertices, setPolygonVertices] = useState<Array<{ xPct: number; yPct: number }>>([]);
  const [activeVertexIndex, setActiveVertexIndex] = useState<number | null>(null);
  const [hoveredVertexIndex, setHoveredVertexIndex] = useState<number | null>(null);
  const [showRegionImportDialog, setShowRegionImportDialog] = useState(false);
  const [regionImportJson, setRegionImportJson] = useState("");
  
  // Phase 4: Copy regions between questions state
  const [copySourceId, setCopySourceId] = useState<string | null>(null);
  
  // Track which URLs have been fetched to avoid re-fetching (ref doesn't trigger re-renders)
  const fetchedUrlsRef = useRef<Set<string>>(new Set());
  // Track question ID to reset cache when question changes
  const lastQuestionIdRef = useRef<string | null>(null);

  // Fetch signed URLs for R2 images when question has image options
  useEffect(() => {
    if (question.type !== "image_choice" || !surveyId || !question.imageOptions?.images) return;

    // Reset fetch cache if question changed (prevents stale cache across different questions)
    // Note: We only clear the ref, not the state - fresh fetches will populate new data
    if (lastQuestionIdRef.current !== question.id) {
      lastQuestionIdRef.current = question.id;
      fetchedUrlsRef.current.clear();
    }

    const imageOptionsImages = question.imageOptions.images;
    
    const fetchSignedUrls = async () => {
      const urls: Record<string, string> = {};
      
      for (const image of imageOptionsImages) {
        if (!image.url) continue;
        
        // Skip if already fetched (using ref to avoid dependency on state)
        if (fetchedUrlsRef.current.has(image.url)) continue;
        
        // Handle external URLs
        if (image.url.startsWith("http://") || image.url.startsWith("https://")) {
          urls[image.url] = image.url;
          fetchedUrlsRef.current.add(image.url);
          continue;
        }
        
        // Fetch signed URL for R2 key
        if (image.url.startsWith("survey-uploads/")) {
          try {
            const apiUrl = API_URL 
              ? `${API_URL}/api/surveys/${surveyId}/files/signed-url`
              : `/api/surveys/${surveyId}/files/signed-url`;
            
            const token = getAuthToken();
            const response = await fetch(apiUrl, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ key: image.url }),
            });

            if (response.ok) {
              const data = await response.json();
              urls[image.url] = data.url;
              fetchedUrlsRef.current.add(image.url);
            }
          } catch (error) {
            console.error("Failed to fetch signed URL:", error);
          }
        }
      }
      
      if (Object.keys(urls).length > 0) {
        setSignedImageUrls(prev => ({ ...prev, ...urls }));
      }
    };

    fetchSignedUrls();
  }, [question.id, question.type, question.imageOptions?.images, surveyId]);

  // Fetch signed URL for heat_map image
  useEffect(() => {
    if (question.type !== "heat_map" || !surveyId || !question.heatMapConfig?.imageKey) return;
    
    const imageKey = question.heatMapConfig.imageKey;
    
    // Skip if already fetched
    if (fetchedUrlsRef.current.has(imageKey)) return;
    
    // Handle external URLs
    if (imageKey.startsWith("http://") || imageKey.startsWith("https://")) {
      setSignedImageUrls(prev => ({ ...prev, [imageKey]: imageKey }));
      fetchedUrlsRef.current.add(imageKey);
      return;
    }
    
    // Fetch signed URL for R2 key
    if (imageKey.startsWith("survey-uploads/")) {
      const fetchHeatMapImageUrl = async () => {
        try {
          const apiUrl = API_URL 
            ? `${API_URL}/api/surveys/${surveyId}/files/signed-url`
            : `/api/surveys/${surveyId}/files/signed-url`;
          
          const token = getAuthToken();
          const response = await fetch(apiUrl, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ key: imageKey }),
          });

          if (response.ok) {
            const data = await response.json();
            setSignedImageUrls(prev => ({ ...prev, [imageKey]: data.url }));
            fetchedUrlsRef.current.add(imageKey);
          }
        } catch (error) {
          console.error("Failed to fetch heat map signed URL:", error);
        }
      };
      
      fetchHeatMapImageUrl();
    }
  }, [question.id, question.type, question.heatMapConfig?.imageKey, surveyId]);

  // Helper to get the display URL for an image
  const getImageDisplayUrl = (url: string): string | null => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return signedImageUrls[url] || null;
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case "multiple_choice":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
      case "single_choice":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
      case "open_text":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
      case "likert_scale":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "dropdown":
        return "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300";
      case "ranking":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300";
      case "rating":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
      case "radio_grid":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "checkbox_grid":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "dropdown_matrix":
        return "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300";
      case "text_matrix":
        return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300";
      case "numeric_matrix":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300";
      case "ranking_matrix":
        return "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300";
      case "date_time":
        return "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300";
      case "file_upload":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
      case "slider_range":
        return "bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300";
      case "video_response":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      case "image_choice":
        return "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300";
      case "heat_map":
        return "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300";
      case "pick_group_rank":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300";
      default:
        return "bg-muted text-foreground";
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    switch (type) {
      case "multiple_choice":
        return "Multiple Choice";
      case "single_choice":
        return "Single Choice";
      case "open_text":
        return "Open Text";
      case "likert_scale":
        return "Likert Scale";
      case "dropdown":
        return "Dropdown";
      case "ranking":
        return "Ranking";
      case "rating":
        return "Rating";
      case "radio_grid":
        return "Radio Grid";
      case "checkbox_grid":
        return "Checkbox Grid";
      case "dropdown_matrix":
        return "Dropdown Matrix";
      case "text_matrix":
        return "Text Matrix";
      case "numeric_matrix":
        return "Numeric Matrix";
      case "ranking_matrix":
        return "Ranking Matrix";
      case "date_time":
        return "Date/Time";
      case "file_upload":
        return "File Upload";
      case "slider_range":
        return "Slider Range";
      case "video_response":
        return "Video Response";
      case "image_choice":
        return "Image Choice";
      case "heat_map":
        return "Heat Map";
      case "pick_group_rank":
        return "Pick, Group & Rank";
      default:
        return type;
    }
  };

  const handleQuestionTextChange = (text: string) => {
    onChange({ ...question, question: text });
  };

  const handleRequiredChange = (required: boolean) => {
    onChange({ ...question, required });
  };

  const handleTypeChange = (type: string) => {
    const updatedQuestion: QuestionType = {
      ...question,
      type: type as QuestionType["type"],
    };

    // Always clear dateTimeConfig unless re-added below for date_time
    // This prevents stale config persisting when switching away from date_time
    delete updatedQuestion.dateTimeConfig;
    // Always clear heatMapConfig unless re-added below for heat_map
    delete updatedQuestion.heatMapConfig;
    // Always clear pickGroupRankConfig unless re-added below for pick_group_rank
    delete updatedQuestion.pickGroupRankConfig;

    // Initialize matrix structure for matrix question types using top-level rows/columns
    if (['radio_grid', 'checkbox_grid', 'dropdown_matrix', 'text_matrix', 'numeric_matrix', 'ranking_matrix'].includes(type)) {
      // Initialize rows as GridRow[] if not already present
      if (!updatedQuestion.rows || updatedQuestion.rows.length === 0) {
        updatedQuestion.rows = [
          { id: `row-${nanoid(10)}`, label: "Row 1", code: null },
          { id: `row-${nanoid(10)}`, label: "Row 2", code: null },
        ];
      }
      // Initialize columns as GridColumn[] if not already present
      if (!updatedQuestion.columns || updatedQuestion.columns.length === 0) {
        updatedQuestion.columns = [
          { id: `col-${nanoid(10)}`, label: "Column 1", code: null },
          { id: `col-${nanoid(10)}`, label: "Column 2", code: null },
        ];
      }
      
      // Initialize dropdown options for dropdown_matrix
      if (type === 'dropdown_matrix' && !updatedQuestion.dropdownMatrixOptions?.shared?.length) {
        updatedQuestion.dropdownMatrixOptions = {
          shared: ["Option 1", "Option 2", "Option 3"]
        };
      }
      
      delete updatedQuestion.options;
      delete updatedQuestion.scale;
    }
    // Reset type-specific properties for simple types
    else if (type === "open_text" || type === "file_upload") {
      delete updatedQuestion.options;
      delete updatedQuestion.scale;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      delete updatedQuestion.sliderRange;
      delete updatedQuestion.videoConfig;
      delete updatedQuestion.imageOptions;
    }
    // Initialize date_time config
    else if (type === "date_time") {
      delete updatedQuestion.options;
      delete updatedQuestion.scale;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      delete updatedQuestion.sliderRange;
      delete updatedQuestion.videoConfig;
      delete updatedQuestion.imageOptions;
      // Always initialize - effectively required for date_time
      updatedQuestion.dateTimeConfig = {
        mode: "date",
        minuteStep: 1,
      };
    }
    // Initialize image choice config
    else if (type === "image_choice") {
      delete updatedQuestion.options;
      delete updatedQuestion.scale;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      delete updatedQuestion.sliderRange;
      delete updatedQuestion.videoConfig;
      if (!updatedQuestion.imageOptions) {
        updatedQuestion.imageOptions = {
          images: [
            { url: "", caption: "" },
            { url: "", caption: "" },
          ],
          selectionMode: "single",
          allowNoneOfAbove: false,
        };
      }
    }
    // Initialize video response config
    else if (type === "video_response") {
      delete updatedQuestion.options;
      delete updatedQuestion.scale;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      delete updatedQuestion.sliderRange;
      if (!updatedQuestion.videoConfig) {
        updatedQuestion.videoConfig = {
          maxDuration: 60,    // Default 1 minute
          allowRetake: true,  // Default allow retakes
        };
      }
    }
    else if (type === "slider_range") {
      delete updatedQuestion.options;
      delete updatedQuestion.scale;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      if (!updatedQuestion.sliderRange) {
        updatedQuestion.sliderRange = {
          min: 0,
          max: 100,
          step: 1,
          labels: ["0", "50", "100"]
        };
      }
    }
    // Initialize heat map config
    else if (type === "heat_map") {
      delete updatedQuestion.options;
      delete updatedQuestion.scale;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      delete updatedQuestion.sliderRange;
      delete updatedQuestion.videoConfig;
      delete updatedQuestion.imageOptions;
      // Initialize with defaults from frozen config
      updatedQuestion.heatMapConfig = { ...DEFAULT_HEAT_MAP_CONFIG };
    }
    // Initialize pick, group & rank config
    else if (type === "pick_group_rank") {
      delete updatedQuestion.options;
      delete updatedQuestion.scale;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      delete updatedQuestion.sliderRange;
      delete updatedQuestion.videoConfig;
      delete updatedQuestion.imageOptions;
      // Deep copy the frozen defaults to avoid mutation
      updatedQuestion.pickGroupRankConfig = {
        ...DEFAULT_PICK_GROUP_RANK_CONFIG,
        items: DEFAULT_PICK_GROUP_RANK_CONFIG.items.map(item => ({ ...item })),
        groups: DEFAULT_PICK_GROUP_RANK_CONFIG.groups.map(group => ({ ...group })),
      };
    } else if (type === "likert_scale" || type === "rating") {
      delete updatedQuestion.options;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      if (!updatedQuestion.scale) {
        updatedQuestion.scale = {
          min: 1,
          max: 5,
          labels: ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"],
        };
      }
    } else {
      delete updatedQuestion.scale;
      delete updatedQuestion.rows;
      delete updatedQuestion.columns;
      if (!updatedQuestion.options) {
        updatedQuestion.options = [
          { id: nanoid(10), text: "Option 1" },
          { id: nanoid(10), text: "Option 2" },
        ];
      }
    }

    onChange(updatedQuestion);
  };

  const handleOptionChange = (index: number, value: string) => {
    if (!question.options) return;
    const newOptions = [...question.options];
    newOptions[index] = { ...newOptions[index], text: value };
    onChange({ ...question, options: newOptions });
  };

  const handleAddOption = () => {
    const newOption: OptionItem = {
      id: nanoid(10),
      text: `Option ${(question.options?.length || 0) + 1}`,
    };
    onChange({ 
      ...question, 
      options: [...(question.options || []), newOption] 
    });
  };

  const handleRemoveOption = (index: number) => {
    if (!question.options) return;
    const removedOption = question.options[index];
    const newOptions = question.options.filter((_, i) => i !== index);
    
    // Remove orphaned optionLogic for the deleted option
    const newOptionLogic = question.optionLogic?.filter(
      ol => ol.optionId !== removedOption.id
    );
    
    onChange({ ...question, options: newOptions, optionLogic: newOptionLogic });
  };

  const handleMoveOptionUp = (index: number) => {
    if (!question.options || index === 0) return;
    const newOptions = [...question.options];
    [newOptions[index - 1], newOptions[index]] = [newOptions[index], newOptions[index - 1]];
    onChange({ ...question, options: newOptions });
  };

  const handleMoveOptionDown = (index: number) => {
    if (!question.options || index === question.options.length - 1) return;
    const newOptions = [...question.options];
    [newOptions[index], newOptions[index + 1]] = [newOptions[index + 1], newOptions[index]];
    onChange({ ...question, options: newOptions });
  };

  // Matrix helper functions - using top-level rows/columns as GridRow[]/GridColumn[]
  const getMatrixRows = (): GridRow[] => question.rows || [];
  const getMatrixColumns = (): GridColumn[] => question.columns || [];

  const handleAddMatrixRow = () => {
    const newRow: GridRow = { id: `row-${nanoid(10)}`, label: "", code: null };
    onChange({ ...question, rows: [...getMatrixRows(), newRow] });
  };

  const handleRemoveMatrixRow = (index: number) => {
    const newRows = getMatrixRows().filter((_, i) => i !== index);
    // Also remove any rowLogic for this row
    const removedRowId = getMatrixRows()[index]?.id;
    const newRowLogic = question.rowLogic?.filter(rl => rl.rowId !== removedRowId);
    onChange({ ...question, rows: newRows, rowLogic: newRowLogic });
  };

  const handleMatrixRowChange = (index: number, value: string) => {
    const newRows = [...getMatrixRows()];
    newRows[index] = { ...newRows[index], label: value };
    onChange({ ...question, rows: newRows });
  };

  const handleAddMatrixColumn = () => {
    const newCol: GridColumn = { id: `col-${nanoid(10)}`, label: "", code: null };
    onChange({ ...question, columns: [...getMatrixColumns(), newCol] });
  };

  const handleRemoveMatrixColumn = (index: number) => {
    const newColumns = getMatrixColumns().filter((_, i) => i !== index);
    onChange({ ...question, columns: newColumns });
  };

  const handleMatrixColumnChange = (index: number, value: string) => {
    const newColumns = [...getMatrixColumns()];
    newColumns[index] = { ...newColumns[index], label: value };
    onChange({ ...question, columns: newColumns });
  };

  const handleMoveMatrixRowUp = (index: number) => {
    const rows = getMatrixRows();
    if (index === 0) return;
    const newRows = [...rows];
    [newRows[index - 1], newRows[index]] = [newRows[index], newRows[index - 1]];
    onChange({ ...question, rows: newRows });
  };

  const handleMoveMatrixRowDown = (index: number) => {
    const rows = getMatrixRows();
    if (index === rows.length - 1) return;
    const newRows = [...rows];
    [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];
    onChange({ ...question, rows: newRows });
  };

  const handleMoveMatrixColumnLeft = (index: number) => {
    const columns = getMatrixColumns();
    if (index === 0) return;
    const newColumns = [...columns];
    [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
    onChange({ ...question, columns: newColumns });
  };

  const handleMoveMatrixColumnRight = (index: number) => {
    const columns = getMatrixColumns();
    if (index === columns.length - 1) return;
    const newColumns = [...columns];
    [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
    onChange({ ...question, columns: newColumns });
  };

  // Dropdown option handlers - using top-level dropdownMatrixOptions
  const handleAddSharedDropdownOption = () => {
    const newOptions = [...(question.dropdownMatrixOptions?.shared || []), "New Option"];
    onChange({
      ...question,
      dropdownMatrixOptions: {
        ...question.dropdownMatrixOptions,
        shared: newOptions
      }
    });
  };

  const handleRemoveSharedDropdownOption = (index: number) => {
    const newOptions = (question.dropdownMatrixOptions?.shared || []).filter((_, i) => i !== index);
    onChange({
      ...question,
      dropdownMatrixOptions: {
        ...question.dropdownMatrixOptions,
        shared: newOptions
      }
    });
  };

  const handleSharedDropdownOptionChange = (index: number, value: string) => {
    const newOptions = [...(question.dropdownMatrixOptions?.shared || [])];
    newOptions[index] = value;
    onChange({
      ...question,
      dropdownMatrixOptions: {
        ...question.dropdownMatrixOptions,
        shared: newOptions
      }
    });
  };

  // Slider range handlers
  const handleSliderRangeChange = (field: 'min' | 'max' | 'step', value: number) => {
    const updatedQuestion: QuestionType = {
      ...question,
      sliderRange: {
        ...question.sliderRange,
        min: question.sliderRange?.min || 0,
        max: question.sliderRange?.max || 100,
        step: question.sliderRange?.step || 1,
        labels: question.sliderRange?.labels || [],
        [field]: value
      }
    };
    onChange(updatedQuestion);
  };

  const handleAddSliderLabel = () => {
    const newLabels = [...(question.sliderRange?.labels || []), "Label"];
    const updatedQuestion: QuestionType = {
      ...question,
      sliderRange: {
        ...question.sliderRange,
        min: question.sliderRange?.min || 0,
        max: question.sliderRange?.max || 100,
        step: question.sliderRange?.step || 1,
        labels: newLabels
      }
    };
    onChange(updatedQuestion);
  };

  const handleRemoveSliderLabel = (index: number) => {
    const newLabels = (question.sliderRange?.labels || []).filter((_, i) => i !== index);
    const updatedQuestion: QuestionType = {
      ...question,
      sliderRange: {
        ...question.sliderRange,
        min: question.sliderRange?.min || 0,
        max: question.sliderRange?.max || 100,
        step: question.sliderRange?.step || 1,
        labels: newLabels
      }
    };
    onChange(updatedQuestion);
  };

  const handleSliderLabelChange = (index: number, value: string) => {
    const newLabels = [...(question.sliderRange?.labels || [])];
    newLabels[index] = value;
    const updatedQuestion: QuestionType = {
      ...question,
      sliderRange: {
        ...question.sliderRange,
        min: question.sliderRange?.min || 0,
        max: question.sliderRange?.max || 100,
        step: question.sliderRange?.step || 1,
        labels: newLabels
      }
    };
    onChange(updatedQuestion);
  };

  // Video config handlers
  const handleVideoMaxDurationChange = (duration: number) => {
    const updatedQuestion: QuestionType = {
      ...question,
      videoConfig: {
        maxDuration: duration,
        allowRetake: question.videoConfig?.allowRetake ?? true,
      }
    };
    onChange(updatedQuestion);
  };

  const handleVideoAllowRetakeChange = (allowRetake: boolean) => {
    const updatedQuestion: QuestionType = {
      ...question,
      videoConfig: {
        maxDuration: question.videoConfig?.maxDuration || 60,
        allowRetake,
      }
    };
    onChange(updatedQuestion);
  };

  // Format duration for display
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  // ============================================
  // Image Choice Handlers
  // ============================================

  const handleAddImageOption = () => {
    const newImages = [
      ...(question.imageOptions?.images || []),
      { url: "", caption: "" }
    ];
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: newImages,
        selectionMode: question.imageOptions?.selectionMode || "single",
      }
    };
    onChange(updatedQuestion);
  };

  const handleRemoveImageOption = (index: number) => {
    const newImages = (question.imageOptions?.images || []).filter((_, i) => i !== index);
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: newImages,
        selectionMode: question.imageOptions?.selectionMode || "single",
      }
    };
    onChange(updatedQuestion);
  };

  const handleImageOptionChange = (index: number, field: "caption" | "altText", value: string) => {
    const newImages = [...(question.imageOptions?.images || [])];
    newImages[index] = { ...newImages[index], [field]: value };
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: newImages,
        selectionMode: question.imageOptions?.selectionMode || "single",
      }
    };
    onChange(updatedQuestion);
  };

  const handleImageUrlChange = (index: number, url: string) => {
    const newImages = [...(question.imageOptions?.images || [])];
    newImages[index] = { ...newImages[index], url };
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: newImages,
        selectionMode: question.imageOptions?.selectionMode || "single",
      }
    };
    onChange(updatedQuestion);
  };

  const handleImageSelectionModeChange = (mode: "single" | "multiple") => {
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: question.imageOptions?.images || [],
        selectionMode: mode,
      }
    };
    onChange(updatedQuestion);
  };

  // Handle actual image upload to the server
  const handleImageUpload = async (index: number, file: File) => {
    if (!surveyId) {
      alert("Cannot upload image: Survey not saved yet. Please save the survey first.");
      return;
    }

    setUploadingImageIndex(index);
    
    try {
      const formData = new FormData();
      formData.append("image", file);
      
      const url = API_URL 
        ? `${API_URL}/api/surveys/${surveyId}/images`
        : `/api/surveys/${surveyId}/images`;
      
      const token = getAuthToken();
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to upload image");
      }

      const result = await response.json();
      
      // Update the image URL with the R2 key
      handleImageUrlChange(index, result.url);
    } catch (error) {
      console.error("Image upload failed:", error);
      alert(error instanceof Error ? error.message : "Failed to upload image. Please try again.");
    } finally {
      setUploadingImageIndex(null);
    }
  };

  const handleNoneOfAboveToggle = (enabled: boolean) => {
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: question.imageOptions?.images || [],
        selectionMode: question.imageOptions?.selectionMode || "single",
        allowNoneOfAbove: enabled,
      }
    };
    onChange(updatedQuestion);
  };

  const handleNoneOfAboveLabelChange = (label: string) => {
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: question.imageOptions?.images || [],
        selectionMode: question.imageOptions?.selectionMode || "single",
        noneOfAboveLabel: label,
      }
    };
    onChange(updatedQuestion);
  };

  const handleMoveImageOptionUp = (index: number) => {
    const images = question.imageOptions?.images || [];
    if (index === 0) return;
    const newImages = [...images];
    [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: newImages,
        selectionMode: question.imageOptions?.selectionMode || "single",
      }
    };
    onChange(updatedQuestion);
  };

  const handleMoveImageOptionDown = (index: number) => {
    const images = question.imageOptions?.images || [];
    if (index === images.length - 1) return;
    const newImages = [...images];
    [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
    const updatedQuestion: QuestionType = {
      ...question,
      imageOptions: {
        ...question.imageOptions,
        images: newImages,
        selectionMode: question.imageOptions?.selectionMode || "single",
      }
    };
    onChange(updatedQuestion);
  };

  // ============================================
  // Heat Map Region Handlers (Phase 2)
  // ============================================
  
  const handleHeatMapConfigUpdate = useCallback((updates: Partial<HeatMapConfig>) => {
    onChange({
      ...question,
      heatMapConfig: {
        ...DEFAULT_HEAT_MAP_CONFIG,
        ...question.heatMapConfig,
        ...updates,
      },
    });
  }, [question, onChange]);

  const handleAddRegion = useCallback(() => {
    const regions = question.heatMapConfig?.regions || [];
    const newRegion: HeatMapRegion = {
      id: nanoid(10),
      name: `Region ${regions.length + 1}`,
      shape: "rect",
      xPct: 25,
      yPct: 25,
      wPct: 50,
      hPct: 50,
    };
    handleHeatMapConfigUpdate({
      regions: [...regions, newRegion],
    });
    setActiveRegionId(newRegion.id);
  }, [question.heatMapConfig?.regions, handleHeatMapConfigUpdate]);

  const handleDeleteRegion = useCallback((regionId: string) => {
    const regions = question.heatMapConfig?.regions || [];
    handleHeatMapConfigUpdate({
      regions: regions.filter((r) => r.id !== regionId),
    });
    if (activeRegionId === regionId) {
      setActiveRegionId(null);
    }
  }, [question.heatMapConfig?.regions, activeRegionId, handleHeatMapConfigUpdate]);

  const handleUpdateRegion = useCallback((regionId: string, updates: Partial<HeatMapRegion>) => {
    const regions = question.heatMapConfig?.regions || [];
    handleHeatMapConfigUpdate({
      regions: regions.map((r) => (r.id === regionId ? { ...r, ...updates } as HeatMapRegion : r)),
    });
  }, [question.heatMapConfig?.regions, handleHeatMapConfigUpdate]);

  const handleRegionNameChange = useCallback((regionId: string, name: string) => {
    handleUpdateRegion(regionId, { name });
  }, [handleUpdateRegion]);

  // Region coordinate helpers - consistent precision and clamping
  const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
  const round2 = (n: number) => Math.round(n * 100) / 100;

  // Calculate percentage from pointer position on image
  const getPercentageFromPointer = useCallback((e: React.PointerEvent<HTMLDivElement>): { xPct: number; yPct: number } | null => {
    if (!regionImageRef.current) return null;
    const rect = regionImageRef.current.getBoundingClientRect();
    const xPct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const yPct = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    return { xPct, yPct };
  }, []);

  // ============================================
  // Phase 4: Polygon Drawing Functions (defined early to avoid circular deps)
  // ============================================
  
  // Add a vertex to the polygon being drawn
  const handleAddPolygonVertex = useCallback((pos: { xPct: number; yPct: number }) => {
    const clampedPos = {
      xPct: round2(clamp(pos.xPct, 0, 100)),
      yPct: round2(clamp(pos.yPct, 0, 100)),
    };
    setPolygonVertices(prev => [...prev, clampedPos]);
  }, []);
  
  // Move a polygon vertex
  const handlePolygonVertexMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (regionDrawMode !== "editing_vertex" || activeVertexIndex === null || !activeRegionId) return;
    
    const pos = getPercentageFromPointer(e);
    if (!pos) return;
    
    const regions = question.heatMapConfig?.regions || [];
    const region = regions.find(r => r.id === activeRegionId);
    if (!region || region.shape !== "poly") return;
    
    const newPoints = [...region.pointsPct];
    newPoints[activeVertexIndex] = {
      xPct: round2(clamp(pos.xPct, 0, 100)),
      yPct: round2(clamp(pos.yPct, 0, 100)),
    };
    
    handleUpdateRegion(activeRegionId, { pointsPct: newPoints });
  }, [regionDrawMode, activeVertexIndex, activeRegionId, getPercentageFromPointer, question.heatMapConfig?.regions, handleUpdateRegion]);
  
  // End vertex editing
  const handlePolygonVertexUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setActiveVertexIndex(null);
    setRegionDrawMode("idle");
    setDrawStartPoint(null);
  }, []);
  
  // Move entire polygon by dragging interior
  const handlePolygonInteriorMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (regionDrawMode !== "moving" || !activeRegionId || !drawStartPoint) return;
    
    const pos = getPercentageFromPointer(e);
    if (!pos) return;
    
    const regions = question.heatMapConfig?.regions || [];
    const region = regions.find(r => r.id === activeRegionId);
    if (!region || region.shape !== "poly") return;
    
    const dx = pos.xPct - drawStartPoint.xPct;
    const dy = pos.yPct - drawStartPoint.yPct;
    
    // Calculate bounding box to clamp movement
    const minX = Math.min(...region.pointsPct.map(p => p.xPct));
    const maxX = Math.max(...region.pointsPct.map(p => p.xPct));
    const minY = Math.min(...region.pointsPct.map(p => p.yPct));
    const maxY = Math.max(...region.pointsPct.map(p => p.yPct));
    
    // Clamp deltas so polygon stays within bounds
    const clampedDx = clamp(dx, -minX, 100 - maxX);
    const clampedDy = clamp(dy, -minY, 100 - maxY);
    
    const newPoints = region.pointsPct.map(p => ({
      xPct: round2(p.xPct + clampedDx),
      yPct: round2(p.yPct + clampedDy),
    }));
    
    handleUpdateRegion(activeRegionId, { pointsPct: newPoints });
    setDrawStartPoint(pos);
  }, [regionDrawMode, activeRegionId, drawStartPoint, getPercentageFromPointer, question.heatMapConfig?.regions, handleUpdateRegion]);

  const handleRegionPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>, regionId: string, handle?: ResizeHandle) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    
    const pos = getPercentageFromPointer(e);
    if (!pos) return;
    
    setActiveRegionId(regionId);
    setDrawStartPoint(pos);
    
    if (handle) {
      setRegionDrawMode("resizing");
      setResizeHandle(handle);
    } else {
      setRegionDrawMode("moving");
    }
  }, [getPercentageFromPointer]);

  const handleImagePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Only start drawing if clicking directly on image (not on a region)
    if (e.target !== e.currentTarget) return;
    
    const pos = getPercentageFromPointer(e);
    if (!pos) return;
    
    // POLYGON MODE: Add vertices on single click
    if (regionShapeMode === "poly") {
      e.preventDefault();
      
      // If not currently drawing, start a new polygon
      if (regionDrawMode !== "drawing_polygon") {
        setRegionDrawMode("drawing_polygon");
        setPolygonVertices([{
          xPct: round2(clamp(pos.xPct, 0, 100)),
          yPct: round2(clamp(pos.yPct, 0, 100)),
        }]);
      } else {
        // Add another vertex
        handleAddPolygonVertex(pos);
      }
      return;
    }
    
    // RECTANGLE MODE: Click and drag
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    
    // Create a new region starting from click point
    const newRegion: HeatMapRegion = {
      id: nanoid(10),
      name: `Region ${(question.heatMapConfig?.regions?.length || 0) + 1}`,
      shape: "rect",
      xPct: pos.xPct,
      yPct: pos.yPct,
      wPct: 0,
      hPct: 0,
    };
    
    handleHeatMapConfigUpdate({
      regions: [...(question.heatMapConfig?.regions || []), newRegion],
    });
    
    setActiveRegionId(newRegion.id);
    setDrawStartPoint(pos);
    setRegionDrawMode("drawing");
  }, [getPercentageFromPointer, regionShapeMode, regionDrawMode, question.heatMapConfig?.regions, handleHeatMapConfigUpdate, handleAddPolygonVertex]);

  const handleRegionPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Handle polygon vertex editing
    if (regionDrawMode === "editing_vertex") {
      handlePolygonVertexMove(e);
      return;
    }
    
    if (regionDrawMode === "idle" || !activeRegionId || !drawStartPoint) return;
    
    const pos = getPercentageFromPointer(e);
    if (!pos) return;
    
    const regions = question.heatMapConfig?.regions || [];
    const region = regions.find((r) => r.id === activeRegionId);
    if (!region) return;
    
    // Handle polygon interior moving
    if (region.shape === "poly" && regionDrawMode === "moving") {
      handlePolygonInteriorMove(e);
      return;
    }
    
    // Rectangle-specific operations
    if (region.shape !== "rect") return;
    
    if (regionDrawMode === "drawing") {
      // Clamp both endpoints to image bounds first, then derive box
      const x1 = clamp(drawStartPoint.xPct, 0, 100);
      const y1 = clamp(drawStartPoint.yPct, 0, 100);
      const x2 = clamp(pos.xPct, 0, 100);
      const y2 = clamp(pos.yPct, 0, 100);

      const xPct = Math.min(x1, x2);
      const yPct = Math.min(y1, y2);
      const wPct = Math.min(Math.abs(x2 - x1), 100 - xPct);
      const hPct = Math.min(Math.abs(y2 - y1), 100 - yPct);

      handleUpdateRegion(activeRegionId, {
        xPct: round2(xPct),
        yPct: round2(yPct),
        wPct: round2(wPct),
        hPct: round2(hPct),
      });
    } else if (regionDrawMode === "moving") {
      // Move region by delta
      const dx = pos.xPct - drawStartPoint.xPct;
      const dy = pos.yPct - drawStartPoint.yPct;
      
      // Clamp to keep region within bounds
      const newX = clamp(region.xPct + dx, 0, 100 - region.wPct);
      const newY = clamp(region.yPct + dy, 0, 100 - region.hPct);
      
      handleUpdateRegion(activeRegionId, { xPct: round2(newX), yPct: round2(newY) });
      setDrawStartPoint(pos);
    } else if (regionDrawMode === "resizing" && resizeHandle) {
      // Resize region based on handle
      let { xPct, yPct, wPct, hPct } = region;
      
      switch (resizeHandle) {
        case "nw":
          wPct = wPct + (xPct - pos.xPct);
          hPct = hPct + (yPct - pos.yPct);
          xPct = pos.xPct;
          yPct = pos.yPct;
          break;
        case "ne":
          wPct = pos.xPct - xPct;
          hPct = hPct + (yPct - pos.yPct);
          yPct = pos.yPct;
          break;
        case "sw":
          wPct = wPct + (xPct - pos.xPct);
          xPct = pos.xPct;
          hPct = pos.yPct - yPct;
          break;
        case "se":
          wPct = pos.xPct - xPct;
          hPct = pos.yPct - yPct;
          break;
        case "n":
          hPct = hPct + (yPct - pos.yPct);
          yPct = pos.yPct;
          break;
        case "s":
          hPct = pos.yPct - yPct;
          break;
        case "w":
          wPct = wPct + (xPct - pos.xPct);
          xPct = pos.xPct;
          break;
        case "e":
          wPct = pos.xPct - xPct;
          break;
      }
      
      // Enforce minimum size and clamp to bounds
      if (wPct >= 2 && hPct >= 2 && xPct >= 0 && yPct >= 0 && xPct + wPct <= 100 && yPct + hPct <= 100) {
        handleUpdateRegion(activeRegionId, {
          xPct: round2(xPct),
          yPct: round2(yPct),
          wPct: round2(wPct),
          hPct: round2(hPct),
        });
      }
    }
  }, [regionDrawMode, activeRegionId, drawStartPoint, resizeHandle, getPercentageFromPointer, question.heatMapConfig?.regions, handleUpdateRegion, handlePolygonVertexMove, handlePolygonInteriorMove]);

  const handleRegionPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Release pointer capture (only if not idle to keep logs clean)
      if (regionDrawMode !== "idle" && regionDrawMode !== "drawing_polygon" && e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      
      // Handle polygon vertex editing mode
      if (regionDrawMode === "editing_vertex") {
        handlePolygonVertexUp(e);
        return;
      }
      
      // Don't reset to idle if we're in polygon drawing mode (vertices are added on click)
      if (regionDrawMode === "drawing_polygon") {
        return;
      }

      if (regionDrawMode === "drawing" && activeRegionId) {
        // Validate region size - delete if too small
        const regions = question.heatMapConfig?.regions || [];
        const region = regions.find((r) => r.id === activeRegionId);
        if (region && region.shape === "rect" && (region.wPct < 2 || region.hPct < 2)) {
          handleDeleteRegion(activeRegionId);
        }
      }

      setRegionDrawMode("idle");
      setResizeHandle(null);
      setDrawStartPoint(null);
    },
    [regionDrawMode, activeRegionId, question.heatMapConfig?.regions, handleDeleteRegion, handlePolygonVertexUp]
  );

  const handleRegionsVisibleToggle = useCallback((visible: boolean) => {
    handleHeatMapConfigUpdate({ regionsVisibleToRespondent: visible });
  }, [handleHeatMapConfigUpdate]);

  // ============================================
  // Phase 4: Additional Polygon Functions
  // ============================================
  
  // Complete the polygon (called on double-click or "Complete" button)
  const handleCompletePolygon = useCallback(() => {
    if (polygonVertices.length < 3) {
      // Not enough vertices - clear and show message
      setPolygonVertices([]);
      setRegionDrawMode("idle");
      return;
    }
    
    // Create the polygon region
    const newRegion: HeatMapRegion = {
      id: nanoid(10),
      name: `Region ${(question.heatMapConfig?.regions?.length || 0) + 1}`,
      shape: "poly",
      pointsPct: polygonVertices,
    };
    
    handleHeatMapConfigUpdate({
      regions: [...(question.heatMapConfig?.regions || []), newRegion],
    });
    
    setPolygonVertices([]);
    setRegionDrawMode("idle");
    setActiveRegionId(newRegion.id);
  }, [polygonVertices, question.heatMapConfig?.regions, handleHeatMapConfigUpdate]);
  
  // Cancel polygon drawing
  const handleCancelPolygonDrawing = useCallback(() => {
    setPolygonVertices([]);
    setRegionDrawMode("idle");
  }, []);
  
  // Start drawing a polygon vertex on the editor
  const handlePolygonVertexPointerDown = useCallback((
    e: React.PointerEvent<HTMLDivElement>,
    regionId: string,
    vertexIndex: number
  ) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    
    setActiveRegionId(regionId);
    setActiveVertexIndex(vertexIndex);
    setRegionDrawMode("editing_vertex");
    
    const pos = getPercentageFromPointer(e);
    if (pos) {
      setDrawStartPoint(pos);
    }
  }, [getPercentageFromPointer]);
  
  // Add a vertex to an existing polygon by clicking on an edge
  const handleAddVertexToEdge = useCallback((regionId: string, afterIndex: number, pos: { xPct: number; yPct: number }) => {
    const regions = question.heatMapConfig?.regions || [];
    const region = regions.find(r => r.id === regionId);
    if (!region || region.shape !== "poly") return;
    
    const newPoints = [...region.pointsPct];
    const clampedPos = {
      xPct: round2(clamp(pos.xPct, 0, 100)),
      yPct: round2(clamp(pos.yPct, 0, 100)),
    };
    newPoints.splice(afterIndex + 1, 0, clampedPos);
    
    handleUpdateRegion(regionId, { pointsPct: newPoints });
  }, [question.heatMapConfig?.regions, handleUpdateRegion]);
  
  // Delete a vertex from a polygon (minimum 3 maintained)
  const handleDeleteVertex = useCallback((regionId: string, vertexIndex: number) => {
    const regions = question.heatMapConfig?.regions || [];
    const region = regions.find(r => r.id === regionId);
    if (!region || region.shape !== "poly") return;
    
    // Don't delete if only 3 vertices remain
    if (region.pointsPct.length <= 3) return;
    
    const newPoints = region.pointsPct.filter((_, idx) => idx !== vertexIndex);
    handleUpdateRegion(regionId, { pointsPct: newPoints });
  }, [question.heatMapConfig?.regions, handleUpdateRegion]);
  
  // ============================================
  // Phase 4: Copy Regions Between Questions
  // ============================================
  
  // Get list of other heat map questions that have regions (for copy feature)
  const heatMapSources = useMemo(() => {
    return (allQuestions ?? [])
      .filter((q) => q.type === "heat_map" && q.id !== question.id)
      .filter((q) => (q.heatMapConfig?.regions?.length ?? 0) > 0);
  }, [allQuestions, question.id]);
  
  // Copy regions from another question
  const copyRegionsFrom = useCallback(
    (sourceQuestionId: string, mode: "replace" | "append") => {
      const source = heatMapSources.find((q) => q.id === sourceQuestionId);
      if (!source?.heatMapConfig?.regions?.length) return;

      // Copy with new IDs (works for both rect and poly due to spread)
      const copied = source.heatMapConfig.regions.map((r) => ({
        ...r,
        id: nanoid(10),
      }));

      const existing = question.heatMapConfig?.regions ?? [];

      handleHeatMapConfigUpdate({
        regions: mode === "replace" ? copied : [...existing, ...copied],
      });
      
      // Reset selection after copy
      setCopySourceId(null);
    },
    [heatMapSources, question.heatMapConfig, handleHeatMapConfigUpdate]
  );

  // ============================================
  // Phase 4: Region Import/Export Functions
  // ============================================
  
  // Export regions to JSON
  const handleExportRegions = useCallback(() => {
    const regions = question.heatMapConfig?.regions || [];
    if (regions.length === 0) return;
    
    const exportData = {
      version: 1,
      exportedAt: new Date().toISOString(),
      regions: regions,
    };
    
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement("a");
    a.href = url;
    a.download = `regions-${question.id}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [question.heatMapConfig?.regions, question.id]);
  
  // Import regions from JSON
  const handleImportRegions = useCallback(() => {
    try {
      const parsed = JSON.parse(regionImportJson);
      
      // Validate structure
      if (!parsed.regions || !Array.isArray(parsed.regions)) {
        alert("Invalid JSON: missing 'regions' array");
        return;
      }
      
      // Validate each region
      const validRegions: HeatMapRegion[] = [];
      for (const region of parsed.regions) {
        if (!region.id || !region.name || !region.shape) {
          alert(`Invalid region: missing required fields (id, name, shape)`);
          return;
        }
        
        if (region.shape === "rect") {
          if (typeof region.xPct !== "number" || typeof region.yPct !== "number" ||
              typeof region.wPct !== "number" || typeof region.hPct !== "number") {
            alert(`Invalid rectangle region '${region.name}': missing position/size fields`);
            return;
          }
          validRegions.push({
            id: nanoid(10),  // Generate new ID to avoid conflicts
            name: region.name,
            shape: "rect",
            xPct: clamp(region.xPct, 0, 100),
            yPct: clamp(region.yPct, 0, 100),
            wPct: clamp(region.wPct, 0, 100 - region.xPct),
            hPct: clamp(region.hPct, 0, 100 - region.yPct),
          });
        } else if (region.shape === "poly") {
          if (!Array.isArray(region.pointsPct) || region.pointsPct.length < 3) {
            alert(`Invalid polygon region '${region.name}': must have at least 3 points`);
            return;
          }
          const validPoints = region.pointsPct.map((p: { xPct: number; yPct: number }) => ({
            xPct: clamp(p.xPct, 0, 100),
            yPct: clamp(p.yPct, 0, 100),
          }));
          validRegions.push({
            id: nanoid(10),
            name: region.name,
            shape: "poly",
            pointsPct: validPoints,
          });
        } else {
          alert(`Unknown region shape: ${region.shape}`);
          return;
        }
      }
      
      // Merge with existing regions
      const existingRegions = question.heatMapConfig?.regions || [];
      handleHeatMapConfigUpdate({
        regions: [...existingRegions, ...validRegions],
      });
      
      setShowRegionImportDialog(false);
      setRegionImportJson("");
    } catch (err) {
      alert("Failed to parse JSON: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  }, [regionImportJson, question.heatMapConfig?.regions, handleHeatMapConfigUpdate]);
  
  // Copy regions from another heat map question in the survey (simplified: just copy from a prop if available)
  // This would need survey-level state to implement fully - for now, export/import covers the use case

  const renderQuestionPreview = () => {
    switch (question.type) {
      case "multiple_choice":
        return (
          <div className="space-y-2 ml-11 pt-2">
            <Label className="text-sm font-medium">Options</Label>
            
            {question.options?.map((option, idx) => (
              <div key={option.id} className="flex items-center gap-2">
                {/* Drag Handle / Reorder buttons */}
                <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                  <button
                    onClick={() => handleMoveOptionUp(idx)}
                    disabled={idx === 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleMoveOptionDown(idx)}
                    disabled={idx === (question.options?.length || 0) - 1}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </div>
                
                {/* Option Input */}
                <Checkbox disabled className="shrink-0" />
                <Input
                  value={option.text}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  placeholder={`Option ${idx + 1}`}
                  className="flex-1"
                />
                
                {/* Remove Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveOption(idx)}
                  aria-label={`Remove option ${idx + 1}`}
                  className="h-8 w-8 shrink-0"
                  disabled={(question.options?.length || 0) <= 2}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            {/* "Other" option preview */}
            {question.allowOther && (
              <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 rounded-md p-2 border border-purple-200 dark:border-purple-700">
                <div className="w-6 shrink-0" /> {/* Spacer for alignment */}
                <Checkbox disabled className="shrink-0" />
                <Input
                  value={question.otherLabel || "Other (please specify)"}
                  onChange={(e) => onChange({ ...question, otherLabel: e.target.value })}
                  placeholder="Other (please specify)"
                  className="flex-1 bg-card"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onChange({ ...question, allowOther: false, otherLabel: undefined })}
                  aria-label="Remove Other option"
                  className="h-8 w-8 shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            {/* Add Option */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddOption}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Option
            </Button>
            
            {/* Allow Other Toggle */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
              <Switch
                id="allow-other-mc"
                checked={question.allowOther || false}
                onCheckedChange={(checked) => onChange({ ...question, allowOther: checked })}
              />
              <Label htmlFor="allow-other-mc" className="text-sm">
                Allow "Other" option
              </Label>
            </div>
          </div>
        );

      case "single_choice":
        return (
          <div className="space-y-2 ml-11 pt-2">
            <Label className="text-sm font-medium">Options</Label>
            
            <RadioGroup disabled>
              {question.options?.map((option, idx) => (
                <div key={option.id} className="flex items-center gap-2">
                  {/* Drag Handle / Reorder buttons */}
                  <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                    <button
                      onClick={() => handleMoveOptionUp(idx)}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleMoveOptionDown(idx)}
                      disabled={idx === (question.options?.length || 0) - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  
                  {/* Option Input */}
                  <RadioGroupItem value={option.id} className="shrink-0" />
                  <Input
                    value={option.text}
                    onChange={(e) => handleOptionChange(idx, e.target.value)}
                    placeholder={`Option ${idx + 1}`}
                    className="flex-1"
                  />
                  
                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveOption(idx)}
                    aria-label={`Remove option ${idx + 1}`}
                    className="h-8 w-8 shrink-0"
                    disabled={(question.options?.length || 0) <= 2}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {/* "Other" option preview */}
              {question.allowOther && (
                <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 rounded-md p-2 border border-purple-200 dark:border-purple-700">
                  <div className="w-6 shrink-0" /> {/* Spacer for alignment */}
                  <RadioGroupItem value="other" disabled className="shrink-0" />
                  <Input
                    value={question.otherLabel || "Other (please specify)"}
                    onChange={(e) => onChange({ ...question, otherLabel: e.target.value })}
                    placeholder="Other (please specify)"
                    className="flex-1 bg-card"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onChange({ ...question, allowOther: false, otherLabel: undefined })}
                    aria-label="Remove Other option"
                    className="h-8 w-8 shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </RadioGroup>
            
            {/* Add Option */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddOption}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Option
            </Button>
            
            {/* Allow Other Toggle */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
              <Switch
                id="allow-other-sc"
                checked={question.allowOther || false}
                onCheckedChange={(checked) => onChange({ ...question, allowOther: checked })}
              />
              <Label htmlFor="allow-other-sc" className="text-sm">
                Allow "Other" option
              </Label>
            </div>
          </div>
        );

      case "open_text":
        return (
          <div className="ml-11 pt-2">
            <Textarea
              placeholder="Respondent will type their answer here..."
              disabled
              className="resize-none"
              rows={3}
            />
          </div>
        );

      case "likert_scale":
        return (
          <div className="ml-11 pt-2">
            <div className="grid grid-cols-5 gap-2 text-center text-sm mb-2">
              {question.scale?.labels?.map((label, idx) => (
                <div key={idx} className="p-2 bg-muted rounded text-xs">
                  {label}
                </div>
              ))}
            </div>
            <RadioGroup disabled className="grid grid-cols-5 gap-2">
              {question.scale?.labels?.map((_, idx) => (
                <div key={idx} className="flex justify-center">
                  <RadioGroupItem value={`scale-${idx}`} />
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case "ranking":
        return (
          <div className="space-y-2 ml-11 pt-2">
            <Label className="text-sm font-medium">Items to Rank</Label>
            <div className="text-sm text-muted-foreground mb-3">
              Respondents will drag items to rank them in order of preference:
            </div>
            
            {question.options?.map((option, idx) => (
              <div key={option.id} className="flex items-center gap-2 bg-muted p-3 rounded-lg border">
                {/* Drag Handle / Reorder buttons */}
                <div className="flex flex-col cursor-grab p-1 rounded hover:bg-background">
                  <button
                    onClick={() => handleMoveOptionUp(idx)}
                    disabled={idx === 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleMoveOptionDown(idx)}
                    disabled={idx === (question.options?.length || 0) - 1}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </div>
                
                {/* Rank Number */}
                <div className="flex items-center justify-center w-6 h-6 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 rounded text-xs font-medium shrink-0">
                  {idx + 1}
                </div>
                
                {/* Option Input */}
                <Input
                  value={option.text}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  placeholder={`Option ${idx + 1}`}
                  className="flex-1 border-none bg-transparent"
                />
                
                {/* Remove Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveOption(idx)}
                  aria-label={`Remove option ${idx + 1}`}
                  className="h-8 w-8 shrink-0"
                  disabled={(question.options?.length || 0) <= 2}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            {/* Add Option */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddOption}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item to Rank
            </Button>
          </div>
        );

      case "dropdown":
        return (
          <div className="space-y-2 ml-11 pt-2">
            <Label className="text-sm font-medium">Options</Label>
            
            {question.options?.map((option, idx) => (
              <div key={option.id} className="flex items-center gap-2">
                {/* Drag Handle / Reorder buttons */}
                <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                  <button
                    onClick={() => handleMoveOptionUp(idx)}
                    disabled={idx === 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleMoveOptionDown(idx)}
                    disabled={idx === (question.options?.length || 0) - 1}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </div>
                
                {/* Option Input */}
                <div className="w-2 h-2 bg-pink-400 rounded-full shrink-0" />
                <Input
                  value={option.text}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  placeholder={`Option ${idx + 1}`}
                  className="flex-1"
                />
                
                {/* Remove Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveOption(idx)}
                  aria-label={`Remove option ${idx + 1}`}
                  className="h-8 w-8 shrink-0"
                  disabled={(question.options?.length || 0) <= 2}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            {/* Add Option */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddOption}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Option
            </Button>
          </div>
        );

      case "rating":
        return (
          <div className="ml-11 pt-2">
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <div key={star} className="text-2xl text-yellow-400">★</div>
              ))}
              <span className="text-sm text-muted-foreground ml-2">5-star rating</span>
            </div>
          </div>
        );

      case "radio_grid":
        return (
          <div className="ml-11 space-y-4 pt-2">
            {/* Rows Editor */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Rows</Label>
              {getMatrixRows().map((row, idx) => (
                <div key={row.id} className="flex items-center gap-2">
                  <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                    <button
                      onClick={() => handleMoveMatrixRowUp(idx)}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleMoveMatrixRowDown(idx)}
                      disabled={idx === getMatrixRows().length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <Input
                    value={row.label}
                    onChange={(e) => handleMatrixRowChange(idx, e.target.value)}
                    placeholder={`Row ${idx + 1}`}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveMatrixRow(idx)}
                    disabled={getMatrixRows().length <= 1}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={handleAddMatrixRow}>
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </div>
            
            {/* Columns Editor */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Columns</Label>
              {getMatrixColumns().map((col, idx) => (
                <div key={col.id} className="flex items-center gap-2">
                  <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                    <button
                      onClick={() => handleMoveMatrixColumnLeft(idx)}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleMoveMatrixColumnRight(idx)}
                      disabled={idx === getMatrixColumns().length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <Input
                    value={col.label}
                    onChange={(e) => handleMatrixColumnChange(idx, e.target.value)}
                    placeholder={`Column ${idx + 1}`}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveMatrixColumn(idx)}
                    disabled={getMatrixColumns().length <= 2}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={handleAddMatrixColumn}>
                <Plus className="h-4 w-4 mr-2" />
                Add Column
              </Button>
            </div>
            
            {/* Preview Table (editable) */}
            <div className="border border-border rounded-lg overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px] min-w-[200px] sticky left-0 z-10 bg-background"></TableHead>
                    {getMatrixColumns().map((col, idx) => (
                      <TableHead key={col.id} className="text-center min-w-[100px] p-1">
                        <Input
                          value={col.label}
                          onChange={(e) => handleMatrixColumnChange(idx, e.target.value)}
                          className="text-center text-xs h-8"
                          placeholder={`Column ${idx + 1}`}
                        />
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getMatrixRows().map((row, rowIdx) => (
                    <TableRow key={row.id}>
                      <TableCell className="p-1 sticky left-0 z-10 bg-background min-w-[200px]">
                        <Input
                          value={row.label}
                          onChange={(e) => handleMatrixRowChange(rowIdx, e.target.value)}
                          className="text-sm h-8"
                          placeholder={`Row ${rowIdx + 1}`}
                        />
                      </TableCell>
                      {getMatrixColumns().map((col) => (
                        <TableCell key={col.id} className="text-center">
                          <input 
                            type="radio" 
                            disabled 
                            className="h-4 w-4 border-border text-primary mx-auto" 
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );

      case "checkbox_grid":
        return (
          <div className="ml-11 space-y-4 pt-2">
            {/* Rows Editor */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Rows</Label>
              {getMatrixRows().map((row, idx) => (
                <div key={row.id} className="flex items-center gap-2">
                  <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                    <button
                      onClick={() => handleMoveMatrixRowUp(idx)}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleMoveMatrixRowDown(idx)}
                      disabled={idx === getMatrixRows().length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <Input
                    value={row.label}
                    onChange={(e) => handleMatrixRowChange(idx, e.target.value)}
                    placeholder={`Row ${idx + 1}`}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveMatrixRow(idx)}
                    disabled={getMatrixRows().length <= 1}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={handleAddMatrixRow}>
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </div>
            
            {/* Columns Editor */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Columns</Label>
              {getMatrixColumns().map((col, idx) => (
                <div key={col.id} className="flex items-center gap-2">
                  <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                    <button
                      onClick={() => handleMoveMatrixColumnLeft(idx)}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleMoveMatrixColumnRight(idx)}
                      disabled={idx === getMatrixColumns().length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <Input
                    value={col.label}
                    onChange={(e) => handleMatrixColumnChange(idx, e.target.value)}
                    placeholder={`Column ${idx + 1}`}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveMatrixColumn(idx)}
                    disabled={getMatrixColumns().length <= 2}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={handleAddMatrixColumn}>
                <Plus className="h-4 w-4 mr-2" />
                Add Column
              </Button>
            </div>
            
            {/* Preview Table (editable) */}
            <div className="border border-border rounded-lg overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px] min-w-[200px] sticky left-0 z-10 bg-background"></TableHead>
                    {getMatrixColumns().map((col, idx) => (
                      <TableHead key={col.id} className="text-center min-w-[100px] p-1">
                        <Input
                          value={col.label}
                          onChange={(e) => handleMatrixColumnChange(idx, e.target.value)}
                          className="text-center text-xs h-8"
                          placeholder={`Column ${idx + 1}`}
                        />
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getMatrixRows().map((row, rowIdx) => (
                    <TableRow key={row.id}>
                      <TableCell className="p-1 sticky left-0 z-10 bg-background min-w-[200px]">
                        <Input
                          value={row.label}
                          onChange={(e) => handleMatrixRowChange(rowIdx, e.target.value)}
                          className="text-sm h-8"
                          placeholder={`Row ${rowIdx + 1}`}
                        />
                      </TableCell>
                      {getMatrixColumns().map((col) => (
                        <TableCell key={col.id} className="text-center">
                          <Checkbox disabled className="mx-auto" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );

      case "date_time": {
        // Defensive fallback prevents crashes if old data lacks config
        const config = question.dateTimeConfig ?? DEFAULT_DATE_TIME_CONFIG;
        const mode = config.mode;
        const inputType = mode === "date" ? "date" : mode === "time" ? "time" : "datetime-local";
        // Only apply step for time inputs (datetime-local can have quirky picker behavior)
        const step = mode === "time" ? (config.minuteStep ?? 1) * 60 : undefined;
        
        const descriptions = {
          date: "Respondents can select a date (YYYY-MM-DD)",
          time: "Respondents can select a time (HH:MM)",
          datetime: "Respondents can select date and time",
        } satisfies Record<DateTimeConfig["mode"], string>;
        
        return (
          <div className="ml-11 space-y-3 pt-2">
            {/* Mode selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Input type:</label>
              <Select
                value={mode}
                onValueChange={(value: "datetime" | "date" | "time") => {
                  onChange({
                    ...question,
                    dateTimeConfig: { 
                      ...config,
                      mode: value 
                    }
                  });
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Date Only</SelectItem>
                  <SelectItem value="time">Time Only</SelectItem>
                  <SelectItem value="datetime">Date & Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Preview input */}
            <input
              type={inputType}
              step={step}
              disabled
              className="w-full p-3 border border-border rounded-lg bg-muted"
            />
            <p className="text-xs text-muted-foreground">{descriptions[mode]}</p>
          </div>
        );
      }

      case "file_upload":
        return (
          <div className="ml-11 pt-2">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center bg-muted">
              <div className="text-muted-foreground mb-2">📎</div>
              <p className="text-sm text-muted-foreground">Click to upload or drag files here</p>
              <p className="text-xs text-muted-foreground mt-1">Supports PDF, DOC, images</p>
            </div>
          </div>
        );

      case "dropdown_matrix":
        return (
          <div className="ml-11 space-y-4 pt-2">
            {/* Dropdown Options Configuration */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Dropdown Options (shared for all cells)</Label>
              <div className="flex flex-wrap gap-2">
                {(question.dropdownMatrixOptions?.shared || []).map((option, idx) => (
                  <div key={idx} className="flex items-center gap-1 bg-card border rounded px-2 py-1">
                    <Input
                      value={option}
                      onChange={(e) => handleSharedDropdownOptionChange(idx, e.target.value)}
                      className="text-sm border-none p-0 h-auto w-24"
                      placeholder="Option"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveSharedDropdownOption(idx)}
                      className="h-6 w-6"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddSharedDropdownOption}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Option
                </Button>
              </div>
            </div>
            
            {/* Rows Editor */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Rows</Label>
              {getMatrixRows().map((row, idx) => (
                <div key={row.id} className="flex items-center gap-2">
                  <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                    <button
                      onClick={() => handleMoveMatrixRowUp(idx)}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleMoveMatrixRowDown(idx)}
                      disabled={idx === getMatrixRows().length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <Input
                    value={row.label}
                    onChange={(e) => handleMatrixRowChange(idx, e.target.value)}
                    placeholder={`Row ${idx + 1}`}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveMatrixRow(idx)}
                    disabled={getMatrixRows().length <= 1}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={handleAddMatrixRow}>
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </div>
            
            {/* Columns Editor */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Columns</Label>
              {getMatrixColumns().map((col, idx) => (
                <div key={col.id} className="flex items-center gap-2">
                  <div className="flex flex-col cursor-grab p-1 rounded hover:bg-muted">
                    <button
                      onClick={() => handleMoveMatrixColumnLeft(idx)}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleMoveMatrixColumnRight(idx)}
                      disabled={idx === getMatrixColumns().length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <Input
                    value={col.label}
                    onChange={(e) => handleMatrixColumnChange(idx, e.target.value)}
                    placeholder={`Column ${idx + 1}`}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveMatrixColumn(idx)}
                    disabled={getMatrixColumns().length <= 2}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={handleAddMatrixColumn}>
                <Plus className="h-4 w-4 mr-2" />
                Add Column
              </Button>
            </div>
            
            {/* Preview Table (editable) */}
            <div className="border border-border rounded-lg overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px] min-w-[200px] sticky left-0 z-10 bg-background"></TableHead>
                    {getMatrixColumns().map((col, idx) => (
                      <TableHead key={col.id} className="text-center min-w-[100px] p-1">
                        <Input
                          value={col.label}
                          onChange={(e) => handleMatrixColumnChange(idx, e.target.value)}
                          className="text-center text-xs h-8"
                          placeholder={`Column ${idx + 1}`}
                        />
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getMatrixRows().map((row, rowIdx) => (
                    <TableRow key={row.id}>
                      <TableCell className="p-1 sticky left-0 z-10 bg-background min-w-[200px]">
                        <Input
                          value={row.label}
                          onChange={(e) => handleMatrixRowChange(rowIdx, e.target.value)}
                          className="text-sm h-8"
                          placeholder={`Row ${rowIdx + 1}`}
                        />
                      </TableCell>
                      {getMatrixColumns().map((col) => (
                        <TableCell key={col.id} className="text-center">
                          <div className="w-[80px] h-8 border border-input rounded-md mx-auto" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );

      case "text_matrix":
      case "numeric_matrix":
      case "ranking_matrix":
        return (
          <div className="ml-11 space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              {question.type === "text_matrix" && "Matrix with text input per cell:"}
              {question.type === "numeric_matrix" && "Matrix with numeric input per cell:"}
              {question.type === "ranking_matrix" && "Matrix where rows are ranked in order:"}
            </p>
            <div className="bg-muted p-4 rounded-lg border">
              <div className="grid gap-3" style={{ gridTemplateColumns: `200px repeat(${getMatrixColumns().length}, 1fr)` }}>
                <div className="font-medium text-sm text-foreground"></div>
                {getMatrixColumns().map((column, idx) => (
                  <div key={column.id} className="flex flex-col items-center space-y-1">
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveMatrixColumnLeft(idx)}
                        disabled={idx === 0}
                        className="text-muted-foreground hover:text-foreground p-0 h-4 w-4 disabled:opacity-30"
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveMatrixColumnRight(idx)}
                        disabled={idx === getMatrixColumns().length - 1}
                        className="text-muted-foreground hover:text-foreground p-0 h-4 w-4 disabled:opacity-30"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMatrixColumn(idx)}
                        className="text-muted-foreground hover:text-red-500 p-0 h-4 w-4"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <Input
                      value={column.label}
                      onChange={(e) => handleMatrixColumnChange(idx, e.target.value)}
                      className="text-xs text-center"
                      placeholder="Column"
                    />
                  </div>
                ))}
                {getMatrixRows().map((row, rowIdx) => [
                  <div key={`row-${row.id}`} className="flex items-center space-x-1">
                    <div className="flex flex-col">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveMatrixRowUp(rowIdx)}
                        disabled={rowIdx === 0}
                        className="text-muted-foreground hover:text-foreground p-0 h-4 disabled:opacity-30"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveMatrixRowDown(rowIdx)}
                        disabled={rowIdx === getMatrixRows().length - 1}
                        className="text-muted-foreground hover:text-foreground p-0 h-4 disabled:opacity-30"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </div>
                    <Input
                      value={row.label}
                      onChange={(e) => handleMatrixRowChange(rowIdx, e.target.value)}
                      className="text-sm flex-1"
                      placeholder="Row label"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMatrixRow(rowIdx)}
                      className="text-muted-foreground hover:text-red-500 p-1"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>,
                  ...getMatrixColumns().map((col, _colIdx) => (
                    <div key={`cell-${row.id}-${col.id}`} className="flex justify-center">
                      {question.type === "text_matrix" && (
                        <input type="text" disabled className="text-xs border rounded p-1 w-full" placeholder="Text..." />
                      )}
                      {question.type === "numeric_matrix" && (
                        <input type="number" disabled className="text-xs border rounded p-1 w-full" placeholder="0" />
                      )}
                      {question.type === "ranking_matrix" && (
                        <select disabled className="text-xs border rounded p-1 bg-card">
                          <option>{rowIdx + 1}</option>
                        </select>
                      )}
                    </div>
                  ))
                ])}
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAddMatrixRow}
                  className="text-primary hover:text-primary/80 text-sm"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Row
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAddMatrixColumn}
                  className="text-primary hover:text-primary/80 text-sm"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Column
                </Button>
              </div>
            </div>
          </div>
        );

      case "slider_range":
        return (
          <div className="ml-11 space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Slider for numeric range selection:
            </p>
            <div className="bg-muted p-4 rounded-lg border space-y-4">
              {/* Slider Configuration */}
              <div className="border-b pb-3">
                <h4 className="text-sm font-medium mb-2">Slider Range Configuration</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">
                      Minimum Value:
                    </label>
                    <Input
                      type="number"
                      value={question.sliderRange?.min || 0}
                      onChange={(e) => handleSliderRangeChange('min', parseInt(e.target.value) || 0)}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">
                      Maximum Value:
                    </label>
                    <Input
                      type="number"
                      value={question.sliderRange?.max || 100}
                      onChange={(e) => handleSliderRangeChange('max', parseInt(e.target.value) || 100)}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">
                      Step Size:
                    </label>
                    <Input
                      type="number"
                      value={question.sliderRange?.step || 1}
                      onChange={(e) => handleSliderRangeChange('step', parseInt(e.target.value) || 1)}
                      className="text-sm"
                      min="1"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-xs font-medium text-foreground mb-1 block">
                    Labels (optional):
                  </label>
                  <div className="flex gap-2">
                    {(question.sliderRange?.labels || []).map((label, idx) => (
                      <div key={idx} className="flex items-center space-x-1 bg-card border rounded px-2 py-1">
                        <Input
                          value={label}
                          onChange={(e) => handleSliderLabelChange(idx, e.target.value)}
                          className="text-xs border-none p-0 h-auto w-16"
                          placeholder="Label"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveSliderLabel(idx)}
                          className="text-muted-foreground hover:text-red-500 p-0 h-auto"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleAddSliderLabel}
                      className="text-primary hover:text-primary/80 text-xs border border-dashed"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Label
                    </Button>
                  </div>
                </div>
              </div>

              {/* Slider Preview */}
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Preview - Current Value:</span>
                  <span className="text-sm font-bold text-primary bg-muted px-2 py-1 rounded">
                    {Math.floor((question.sliderRange?.min || 0) + ((question.sliderRange?.max || 100) - (question.sliderRange?.min || 0)) / 2)}
                  </span>
                </div>
                <input
                  type="range"
                  min={question.sliderRange?.min || 0}
                  max={question.sliderRange?.max || 100}
                  step={question.sliderRange?.step || 1}
                  defaultValue={(question.sliderRange?.min || 0) + ((question.sliderRange?.max || 100) - (question.sliderRange?.min || 0)) / 2}
                  disabled
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer opacity-50"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  {question.sliderRange?.labels && question.sliderRange.labels.length > 0 ? (
                    question.sliderRange.labels.map((label, idx) => (
                      <span key={idx}>{label}</span>
                    ))
                  ) : (
                    <>
                      <span>{question.sliderRange?.min || 0}</span>
                      <span>{Math.floor(((question.sliderRange?.min || 0) + (question.sliderRange?.max || 100)) / 2)}</span>
                      <span>{question.sliderRange?.max || 100}</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Range: {question.sliderRange?.min || 0} to {question.sliderRange?.max || 100}, 
                  Step: {question.sliderRange?.step || 1}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 italic">
                  Note: In the actual survey, respondents will see the value update in real-time as they drag the slider.
                </p>
              </div>
            </div>
          </div>
        );

      case "video_response":
        return (
          <div className="ml-11 space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Respondents will record a video response:
            </p>
            <div className="bg-muted p-4 rounded-lg border space-y-4">
              {/* Video Configuration */}
              <div className="border-b pb-3">
                <h4 className="text-sm font-medium mb-3">Video Recording Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">
                      Maximum Duration:
                    </label>
                    <Select 
                      value={String(question.videoConfig?.maxDuration || 60)} 
                      onValueChange={(v) => handleVideoMaxDurationChange(parseInt(v))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 seconds</SelectItem>
                        <SelectItem value="60">1 minute</SelectItem>
                        <SelectItem value="120">2 minutes</SelectItem>
                        <SelectItem value="180">3 minutes</SelectItem>
                        <SelectItem value="300">5 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2 pt-5">
                    <Checkbox 
                      id={`allow-retake-${question.id}`}
                      checked={question.videoConfig?.allowRetake ?? true}
                      onCheckedChange={(checked) => handleVideoAllowRetakeChange(checked as boolean)}
                    />
                    <Label 
                      htmlFor={`allow-retake-${question.id}`}
                      className="text-sm font-medium text-foreground cursor-pointer"
                    >
                      Allow retakes
                    </Label>
                  </div>
                </div>
              </div>

              {/* Video Preview Placeholder */}
              <div className="relative aspect-video bg-slate-800 dark:bg-slate-900 rounded-lg overflow-hidden flex flex-col items-center justify-center">
                <Video className="w-16 h-16 text-slate-500 dark:text-slate-400 mb-3" />
                <p className="text-slate-400 dark:text-slate-500 text-sm">Video Recording Preview</p>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                  Max duration: {formatDuration(question.videoConfig?.maxDuration || 60)}
                </p>
              </div>

              {/* Mobile Compatibility Note */}
              <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <Smartphone className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <span className="font-medium">Mobile compatible:</span> On phones, respondents will use their device's native camera app for the best recording experience.
                </div>
              </div>
            </div>
          </div>
        );

      case "image_choice":
        return (
          <div className="ml-11 space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Respondents will select from image options:
            </p>
            <div className="bg-muted p-4 rounded-lg border space-y-4">
              {/* Image Choice Configuration */}
              <div className="border-b pb-3">
                <h4 className="text-sm font-medium mb-3">Image Choice Settings</h4>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <Label className="text-xs font-medium text-foreground">Selection Mode:</Label>
                    <Select 
                      value={question.imageOptions?.selectionMode || "single"} 
                      onValueChange={(v) => handleImageSelectionModeChange(v as "single" | "multiple")}
                    >
                      <SelectTrigger className="w-36 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single Select</SelectItem>
                        <SelectItem value="multiple">Multiple Select</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id={`none-of-above-${question.id}`}
                      checked={question.imageOptions?.allowNoneOfAbove ?? false}
                      onCheckedChange={(checked) => handleNoneOfAboveToggle(checked as boolean)}
                    />
                    <Label 
                      htmlFor={`none-of-above-${question.id}`}
                      className="text-xs font-medium text-foreground cursor-pointer"
                    >
                      Add "None of the above" option
                    </Label>
                  </div>
                </div>
                {question.imageOptions?.allowNoneOfAbove && (
                  <div className="mt-2">
                    <Label className="text-xs font-medium text-foreground mb-1 block">Custom label (optional):</Label>
                    <Input
                      value={question.imageOptions?.noneOfAboveLabel || ""}
                      onChange={(e) => handleNoneOfAboveLabelChange(e.target.value)}
                      placeholder="None of the above"
                      className="w-64 h-8 text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Image Options List */}
              <div className="space-y-3">
                <Label className="text-xs font-medium text-foreground">Image Options:</Label>
                {(question.imageOptions?.images || []).map((imageOpt, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-card border rounded-lg">
                    {/* Reorder buttons */}
                    <div className="flex flex-col pt-6">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveImageOptionUp(idx)}
                        disabled={idx === 0}
                        className="text-muted-foreground hover:text-foreground p-0 h-5 disabled:opacity-30"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveImageOptionDown(idx)}
                        disabled={idx === (question.imageOptions?.images?.length || 0) - 1}
                        className="text-muted-foreground hover:text-foreground p-0 h-5 disabled:opacity-30"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Selection indicator (visual only - shows what respondent will see) */}
                    <div className="pt-6">
                      {question.imageOptions?.selectionMode === "multiple" ? (
                        <Checkbox disabled className="mt-1" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-border mt-1" />
                      )}
                    </div>

                    {/* Image preview/upload area */}
                    {(() => {
                      const displayUrl = imageOpt.url ? getImageDisplayUrl(imageOpt.url) : null;
                      return (
                        <div className="w-32 h-24 bg-muted border-2 border-dashed border-border rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 relative">
                          {imageOpt.url && displayUrl ? (
                            <img 
                              src={displayUrl} 
                              alt={imageOpt.altText || imageOpt.caption || `Option ${idx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : imageOpt.url && !displayUrl ? (
                            <div className="flex flex-col items-center text-muted-foreground">
                              <span className="animate-spin h-5 w-5 border-2 border-border border-t-primary rounded-full mb-1" />
                              <span className="text-xs">Loading...</span>
                            </div>
                          ) : null}
                          <div className={`flex flex-col items-center text-muted-foreground ${imageOpt.url ? 'hidden' : ''}`}>
                            <ImageIcon className="h-8 w-8 mb-1" />
                            <span className="text-xs">No image</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Caption and URL inputs */}
                    <div className="flex-1 space-y-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Image URL or upload:</Label>
                        <div className="flex gap-2">
                          <Input
                            value={imageOpt.url || ""}
                            onChange={(e) => handleImageUrlChange(idx, e.target.value)}
                            placeholder="Enter image URL or upload..."
                            className="flex-1 h-8 text-sm"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-3 text-xs"
                            disabled={uploadingImageIndex === idx}
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/jpeg,image/png,image/gif,image/webp';
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0];
                                if (file) {
                                  handleImageUpload(idx, file);
                                }
                              };
                              input.click();
                            }}
                          >
                            {uploadingImageIndex === idx ? (
                              <>
                                <span className="animate-spin h-3 w-3 mr-1 border-2 border-border border-t-primary rounded-full" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-3 w-3 mr-1" />
                                Upload
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Caption (optional):</Label>
                        <Input
                          value={imageOpt.caption || ""}
                          onChange={(e) => handleImageOptionChange(idx, "caption", e.target.value)}
                          placeholder="Enter image caption..."
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id={`alt-text-${question.id}-${idx}`}
                          checked={imageOpt.altText !== undefined}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              // Enable alt text - set to empty string to show input
                              handleImageOptionChange(idx, "altText", "");
                            } else {
                              // Disable alt text - set to undefined by removing it
                              const newImages = [...(question.imageOptions?.images || [])];
                              // eslint-disable-next-line @typescript-eslint/no-unused-vars
                              const { altText: _altText, ...rest } = newImages[idx];
                              newImages[idx] = rest as typeof newImages[number];
                              onChange({
                                ...question,
                                imageOptions: {
                                  ...question.imageOptions,
                                  images: newImages,
                                  selectionMode: question.imageOptions?.selectionMode || "single",
                                }
                              });
                            }
                          }}
                        />
                        <Label 
                          htmlFor={`alt-text-${question.id}-${idx}`}
                          className="text-xs text-muted-foreground cursor-pointer"
                        >
                          Include descriptive text for accessibility
                        </Label>
                      </div>
                      {imageOpt.altText !== undefined && (
                        <Input
                          value={imageOpt.altText || ""}
                          onChange={(e) => handleImageOptionChange(idx, "altText", e.target.value)}
                          placeholder="Describe this image for screen readers..."
                          className="h-8 text-sm"
                        />
                      )}
                    </div>

                    {/* Delete button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveImageOption(idx)}
                      className="text-muted-foreground hover:text-red-500 p-1 mt-5"
                      disabled={(question.imageOptions?.images?.length || 0) <= 2}
                      title={(question.imageOptions?.images?.length || 0) <= 2 ? "Minimum 2 options required" : "Remove option"}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                {/* Add Image Option Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAddImageOption}
                  className="text-primary hover:text-primary/80 text-sm font-medium flex items-center space-x-1 mt-2"
                >
                  <ImagePlus className="h-4 w-4 mr-1" />
                  Add Image Option
                </Button>
              </div>

              {/* Preview Note */}
              <div className="flex items-start gap-2 bg-sky-50 p-3 rounded-lg">
                <ImageIcon className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-sky-700">
                  <span className="font-medium">Preview:</span> Images will be displayed in a grid layout. Respondents can click on images to select {question.imageOptions?.selectionMode === "multiple" ? "multiple options" : "one option"}.
                </div>
              </div>
            </div>
          </div>
        );

      case "heat_map":
        return (
          <div className="ml-11 space-y-4 pt-2">
            {/* Image Upload or Heat Map Editor */}
            {!question.heatMapConfig?.imageKey ? (
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  Upload an image for participants to click on
                </p>
                <Button
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/jpeg,image/png,image/gif,image/webp';
                    input.onchange = async (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (!file || !surveyId) return;
                      
                      setIsUploadingHeatMapImage(true);
                      try {
                        const formData = new FormData();
                        formData.append('image', file);

                        const apiUrl = API_URL 
                          ? `${API_URL}/api/surveys/${surveyId}/images`
                          : `/api/surveys/${surveyId}/images`;
                        
                        const token = getAuthToken();
                        const response = await fetch(apiUrl, {
                          method: 'POST',
                          credentials: 'include',
                          headers: token ? { Authorization: `Bearer ${token}` } : {},
                          body: formData,
                        });

                        if (response.ok) {
                          const data = await response.json();
                          const img = new Image();
                          img.onload = () => {
                            onChange({
                              ...question,
                              heatMapConfig: {
                                ...question.heatMapConfig,
                                imageKey: data.url,
                                maxClicks: question.heatMapConfig?.maxClicks ?? 1,
                                replaceOldest: question.heatMapConfig?.replaceOldest ?? true,
                                allowDeselect: question.heatMapConfig?.allowDeselect ?? true,
                                imageNaturalWidth: img.naturalWidth,
                                imageNaturalHeight: img.naturalHeight,
                              }
                            });
                          };
                          img.src = URL.createObjectURL(file);
                        }
                      } catch (error) {
                        console.error('Failed to upload heat map image:', error);
                      } finally {
                        setIsUploadingHeatMapImage(false);
                      }
                    };
                    input.click();
                  }}
                  disabled={isUploadingHeatMapImage}
                >
                  {isUploadingHeatMapImage ? (
                    <>
                      <span className="animate-spin h-4 w-4 mr-2 border-2 border-border border-t-primary rounded-full" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <ImagePlus className="h-4 w-4 mr-2" />
                      Upload Image
                    </>
                  )}
                </Button>
              </div>
            ) : (
            <div className="space-y-4">
              {/* Heat Map Configuration */}
              <div className="border-b pb-3">
                <h4 className="text-sm font-medium mb-3">Heat Map Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Label className="text-xs font-medium text-foreground whitespace-nowrap">Max Clicks:</Label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={question.heatMapConfig?.maxClicks ?? 1}
                      onChange={(e) => {
                        const maxClicks = Math.max(1, Math.min(20, parseInt(e.target.value) || 1));
                        onChange({
                          ...question,
                          heatMapConfig: {
                            ...question.heatMapConfig,
                            imageKey: question.heatMapConfig?.imageKey || "",
                            maxClicks,
                            replaceOldest: question.heatMapConfig?.replaceOldest ?? true,
                            allowDeselect: question.heatMapConfig?.allowDeselect ?? true,
                          }
                        });
                      }}
                      className="w-20 h-8 text-sm"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id={`replace-oldest-${question.id}`}
                      checked={question.heatMapConfig?.replaceOldest ?? true}
                      onCheckedChange={(checked) => {
                        onChange({
                          ...question,
                          heatMapConfig: {
                            ...question.heatMapConfig,
                            imageKey: question.heatMapConfig?.imageKey || "",
                            maxClicks: question.heatMapConfig?.maxClicks ?? 1,
                            replaceOldest: checked as boolean,
                            allowDeselect: question.heatMapConfig?.allowDeselect ?? true,
                          }
                        });
                      }}
                    />
                    <Label 
                      htmlFor={`replace-oldest-${question.id}`}
                      className="text-xs font-medium text-foreground cursor-pointer"
                    >
                      Replace oldest click when max reached
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id={`allow-deselect-${question.id}`}
                      checked={question.heatMapConfig?.allowDeselect ?? true}
                      onCheckedChange={(checked) => {
                        onChange({
                          ...question,
                          heatMapConfig: {
                            ...question.heatMapConfig,
                            imageKey: question.heatMapConfig?.imageKey || "",
                            maxClicks: question.heatMapConfig?.maxClicks ?? 1,
                            replaceOldest: question.heatMapConfig?.replaceOldest ?? true,
                            allowDeselect: checked as boolean,
                          }
                        });
                      }}
                    />
                    <Label 
                      htmlFor={`allow-deselect-${question.id}`}
                      className="text-xs font-medium text-foreground cursor-pointer"
                    >
                      Allow click deselection (click dot to remove)
                    </Label>
                  </div>
                </div>
              </div>

              {/* Image Upload/Preview */}
              <div className="space-y-3">
                <Label className="text-xs font-medium text-foreground">Heat Map Image:</Label>
                <div className="flex gap-4">
                  {/* Image preview area */}
                  <div className="w-48 h-36 bg-muted border-2 border-dashed border-border rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 relative">
                    {question.heatMapConfig?.imageKey && signedImageUrls[question.heatMapConfig.imageKey] ? (
                      <img 
                        src={signedImageUrls[question.heatMapConfig.imageKey]} 
                        alt="Heat map target"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : question.heatMapConfig?.imageKey && !signedImageUrls[question.heatMapConfig.imageKey] ? (
                      <div className="flex flex-col items-center text-muted-foreground">
                        <span className="animate-spin h-5 w-5 border-2 border-border border-t-primary rounded-full mb-1" />
                        <span className="text-xs">Loading...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center text-muted-foreground">
                        <Target className="h-10 w-10 mb-2" />
                        <span className="text-xs text-center px-2">No image uploaded</span>
                      </div>
                    )}
                  </div>

                  {/* Upload controls */}
                  <div className="flex-1 space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Image URL or upload:</Label>
                      <div className="flex gap-2">
                        <Input
                          value={question.heatMapConfig?.imageKey || ""}
                          onChange={(e) => {
                            const imageKey = e.target.value;
                            onChange({
                              ...question,
                              heatMapConfig: {
                                ...question.heatMapConfig,
                                imageKey,
                                maxClicks: question.heatMapConfig?.maxClicks ?? 1,
                                replaceOldest: question.heatMapConfig?.replaceOldest ?? true,
                                allowDeselect: question.heatMapConfig?.allowDeselect ?? true,
                              }
                            });
                          }}
                          placeholder="Enter image URL or upload..."
                          className="flex-1 h-8 text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs"
                          disabled={isUploadingHeatMapImage}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/jpeg,image/png,image/gif,image/webp';
                            input.onchange = async (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (!file || !surveyId) return;
                              
                              setIsUploadingHeatMapImage(true);
                              try {
                                const formData = new FormData();
                                formData.append('image', file);

                                const apiUrl = API_URL 
                                  ? `${API_URL}/api/surveys/${surveyId}/images`
                                  : `/api/surveys/${surveyId}/images`;
                                
                                const token = getAuthToken();
                                const response = await fetch(apiUrl, {
                                  method: 'POST',
                                  credentials: 'include',
                                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                                  body: formData,
                                });

                                if (response.ok) {
                                  const data = await response.json();
                                  // Create an image element to get natural dimensions
                                  const img = new Image();
                                  img.onload = () => {
                                    onChange({
                                      ...question,
                                      heatMapConfig: {
                                        ...question.heatMapConfig,
                                        imageKey: data.url,  // Backend returns 'url' not 'key'
                                        maxClicks: question.heatMapConfig?.maxClicks ?? 1,
                                        replaceOldest: question.heatMapConfig?.replaceOldest ?? true,
                                        allowDeselect: question.heatMapConfig?.allowDeselect ?? true,
                                        imageNaturalWidth: img.naturalWidth,
                                        imageNaturalHeight: img.naturalHeight,
                                      }
                                    });
                                  };
                                  img.src = URL.createObjectURL(file);
                                } else {
                                  console.error('Failed to upload heat map image');
                                }
                              } catch (error) {
                                console.error('Failed to upload heat map image:', error);
                              } finally {
                                setIsUploadingHeatMapImage(false);
                              }
                            };
                            input.click();
                          }}
                        >
                          {isUploadingHeatMapImage ? (
                            <>
                              <span className="animate-spin h-3 w-3 mr-1 border-2 border-border border-t-primary rounded-full" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-3 w-3 mr-1" />
                              Upload
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    {question.heatMapConfig?.imageNaturalWidth && question.heatMapConfig?.imageNaturalHeight && (
                      <div className="text-xs text-muted-foreground">
                        Image dimensions: {question.heatMapConfig.imageNaturalWidth} × {question.heatMapConfig.imageNaturalHeight}px
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Region Builder (Phase 2 + Phase 4) */}
              {question.heatMapConfig?.imageKey && signedImageUrls[question.heatMapConfig.imageKey] && (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Square className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-xs font-medium text-foreground">Reporting Regions</Label>
                      <span className="text-xs text-muted-foreground">
                        ({(question.heatMapConfig?.regions?.length || 0)} defined)
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Shape Mode Toggle */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant={regionShapeMode === "rect" ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setRegionShapeMode("rect");
                            if (regionDrawMode === "drawing_polygon") {
                              handleCancelPolygonDrawing();
                            }
                          }}
                        >
                          <Square className="h-4 w-4 mr-1" />
                          Rectangle
                        </Button>
                        <Button
                          variant={regionShapeMode === "poly" ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setRegionShapeMode("poly");
                          }}
                        >
                          <Hexagon className="h-4 w-4 mr-1" />
                          Polygon
                        </Button>
                      </div>
                      
                      {/* Visibility toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`regions-visible-${question.id}`}
                          checked={question.heatMapConfig?.regionsVisibleToRespondent ?? false}
                          onCheckedChange={handleRegionsVisibleToggle}
                          className="scale-75"
                        />
                        <Label
                          htmlFor={`regions-visible-${question.id}`}
                          className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1"
                        >
                          {question.heatMapConfig?.regionsVisibleToRespondent ? (
                            <><Eye className="h-3 w-3" /> Show to respondents</>
                          ) : (
                            <><EyeOff className="h-3 w-3" /> Hidden from respondents</>
                          )}
                        </Label>
                      </div>
                      
                      {/* Import/Export */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleExportRegions}
                          disabled={(question.heatMapConfig?.regions?.length || 0) === 0}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Export
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowRegionImportDialog(true)}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Import
                        </Button>
                      </div>
                      
                      {/* Copy from another question (Phase 4) */}
                      {heatMapSources.length > 0 && (
                        <div className="flex items-center gap-1" title="Regions are stored as percentages. If images differ, regions may not line up.">
                          <Select
                            value={copySourceId || ""}
                            onValueChange={(value) => setCopySourceId(value || null)}
                          >
                            <SelectTrigger className="h-7 text-xs w-[140px]">
                              <SelectValue placeholder="Copy from..." />
                            </SelectTrigger>
                            <SelectContent>
                              {heatMapSources.map((q) => (
                                <SelectItem key={q.id} value={q.id}>
                                  <span className="truncate max-w-[200px]">
                                    {q.question.substring(0, 25)}{q.question.length > 25 ? '...' : ''} ({q.heatMapConfig?.regions?.length})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => copySourceId && copyRegionsFrom(copySourceId, "replace")}
                            disabled={!copySourceId}
                            className="h-7 text-xs"
                            title="Replace current regions with copied ones"
                          >
                            Replace
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copySourceId && copyRegionsFrom(copySourceId, "append")}
                            disabled={!copySourceId}
                            className="h-7 text-xs"
                            title="Append copied regions to existing ones"
                          >
                            Append
                          </Button>
                        </div>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddRegion}
                        className="h-7 text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Region
                      </Button>
                    </div>
                  </div>

                  {/* Interactive Region Editor */}
                  <div 
                    ref={regionImageRef}
                    className="relative inline-block w-full border rounded-lg overflow-hidden bg-muted select-none"
                    style={{ touchAction: (regionDrawMode === "idle" || regionDrawMode === "drawing_polygon") ? "auto" : "none" }}
                    onPointerDown={handleImagePointerDown}
                    onPointerMove={handleRegionPointerMove}
                    onPointerUp={handleRegionPointerUp}
                    onPointerLeave={handleRegionPointerUp}
                    onPointerCancel={handleRegionPointerUp}
                    onDoubleClick={() => {
                      // Double-click to complete polygon (Phase 4)
                      if (regionDrawMode === "drawing_polygon" && polygonVertices.length >= 3) {
                        handleCompletePolygon();
                      }
                    }}
                  >
                    <img
                      src={signedImageUrls[question.heatMapConfig.imageKey]}
                      alt="Region editor"
                      className="block w-full h-auto pointer-events-none"
                      draggable={false}
                    />
                    
                    {/* Render regions as overlays */}
                    {(question.heatMapConfig?.regions || []).map((region) => {
                      const isActive = activeRegionId === region.id;
                      
                      // Render RECTANGLE regions
                      if (region.shape === "rect") {
                        return (
                          <div
                            key={region.id}
                            className={cn(
                              "absolute border-2 rounded-sm transition-all cursor-move",
                              isActive
                                ? "border-primary ring-2 ring-primary/20"
                                : "border-blue-500"
                            )}
                            style={{
                              left: `${region.xPct}%`,
                              top: `${region.yPct}%`,
                              width: `${region.wPct}%`,
                              height: `${region.hPct}%`,
                            }}
                            onPointerDown={(e) => handleRegionPointerDown(e, region.id)}
                          >
                            {/* Region name label */}
                            <span 
                              className="absolute -top-6 left-0 text-xs bg-background px-1 rounded border border-border whitespace-nowrap"
                            >
                              {region.name}
                            </span>
                            
                            {/* Resize handles (only show for active region) */}
                            {isActive && (
                              <>
                                {["nw", "ne", "sw", "se"].map((handle) => (
                                  <div
                                    key={handle}
                                    className={cn(
                                      "absolute w-3 h-3 bg-primary rounded-full cursor-resize",
                                      handle.includes("n") ? "-top-1.5" : "-bottom-1.5",
                                      handle.includes("w") ? "-left-1.5" : "-right-1.5"
                                    )}
                                    onPointerDown={(e) => handleRegionPointerDown(e, region.id, handle as "nw" | "ne" | "sw" | "se")}
                                  />
                                ))}
                              </>
                            )}
                          </div>
                        );
                      }
                      
                      // Render POLYGON regions
                      if (region.shape === "poly" && region.pointsPct.length >= 3) {
                        const points = region.pointsPct;
                        const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.xPct} ${p.yPct}`).join(' ') + ' Z';
                        
                        // Calculate centroid for label positioning
                        const centroidX = points.reduce((sum, p) => sum + p.xPct, 0) / points.length;
                        const centroidY = points.reduce((sum, p) => sum + p.yPct, 0) / points.length;
                        
                        return (
                          <React.Fragment key={region.id}>
                            {/* SVG overlay for polygon shape */}
                            <svg
                              className="absolute inset-0 w-full h-full pointer-events-none"
                              viewBox="0 0 100 100"
                              preserveAspectRatio="none"
                              style={{ overflow: 'visible' }}
                            >
                              <path
                                d={pathData}
                                fill={isActive ? 'hsl(var(--primary) / 0.2)' : 'rgba(59, 130, 246, 0.15)'}
                                stroke={isActive ? 'hsl(var(--primary))' : '#3b82f6'}
                                strokeWidth="2"
                                className="cursor-move pointer-events-auto"
                                onPointerDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  regionImageRef.current?.setPointerCapture(e.pointerId);
                                  setActiveRegionId(region.id);
                                  const pos = getPercentageFromPointer(e as unknown as React.PointerEvent<HTMLDivElement>);
                                  if (pos) {
                                    setDrawStartPoint(pos);
                                    setRegionDrawMode("moving");
                                  }
                                }}
                              />
                            </svg>
                            
                            {/* Region name label at centroid */}
                            <span 
                              className="absolute text-xs bg-background px-1 rounded border border-border pointer-events-none whitespace-nowrap"
                              style={{ 
                                left: `${centroidX}%`, 
                                top: `${centroidY}%`,
                                transform: 'translate(-50%, -50%)',
                              }}
                            >
                              {region.name}
                            </span>
                            
                            {/* Vertex handles (only show for active polygon) */}
                            {isActive && points.map((point, idx) => (
                              <div
                                key={`vertex-${idx}`}
                                className={cn(
                                  "absolute w-3 h-3 rounded-full border-2 cursor-move transform -translate-x-1/2 -translate-y-1/2",
                                  hoveredVertexIndex === idx ? 'bg-primary border-background scale-125' : 'bg-background border-primary'
                                )}
                                style={{
                                  left: `${point.xPct}%`,
                                  top: `${point.yPct}%`,
                                }}
                                onPointerDown={(e) => handlePolygonVertexPointerDown(e, region.id, idx)}
                                onPointerEnter={() => setHoveredVertexIndex(idx)}
                                onPointerLeave={() => setHoveredVertexIndex(null)}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  if (points.length > 3) {
                                    handleDeleteVertex(region.id, idx);
                                  }
                                }}
                                title={points.length > 3 ? "Right-click to delete" : "Minimum 3 vertices required"}
                              />
                            ))}
                            
                            {/* Edge midpoint handles (click to add vertex) */}
                            {isActive && points.map((point, idx) => {
                              const nextIdx = (idx + 1) % points.length;
                              const nextPoint = points[nextIdx];
                              const midX = (point.xPct + nextPoint.xPct) / 2;
                              const midY = (point.yPct + nextPoint.yPct) / 2;
                              return (
                                <div
                                  key={`midpoint-${idx}`}
                                  className="absolute w-2.5 h-2.5 rounded-full bg-primary/30 border border-primary/50 cursor-pointer transform -translate-x-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 hover:scale-125 transition-all"
                                  style={{
                                    left: `${midX}%`,
                                    top: `${midY}%`,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddVertexToEdge(region.id, idx, { xPct: midX, yPct: midY });
                                  }}
                                  title="Click to add vertex here"
                                />
                              );
                            })}
                          </React.Fragment>
                        );
                      }
                      
                      return null;
                    })}
                    
                    {/* In-progress polygon preview (Phase 4) */}
                    {regionDrawMode === "drawing_polygon" && polygonVertices.length > 0 && (
                      <>
                        {/* Draw lines connecting vertices - viewBox 0-100 maps to container */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: 'visible' }}>
                          <polyline
                            points={polygonVertices.map(p => `${p.xPct} ${p.yPct}`).join(' ')}
                            fill="none"
                            stroke="#d946ef"
                            strokeWidth="2"
                            strokeDasharray="5,5"
                          />
                        </svg>
                        
                        {/* Draw vertex dots */}
                        {polygonVertices.map((vertex, idx) => (
                          <div
                            key={`preview-vertex-${idx}`}
                            className="absolute w-3 h-3 bg-fuchsia-500 border-2 border-white rounded-full transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                            style={{
                              left: `${vertex.xPct}%`,
                              top: `${vertex.yPct}%`,
                            }}
                          />
                        ))}
                      </>
                    )}
                    
                    {/* Instructions overlay */}
                    {(question.heatMapConfig?.regions?.length || 0) === 0 && regionDrawMode === "idle" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10 pointer-events-none">
                        <div className="bg-card/90 px-3 py-2 rounded-lg shadow text-sm text-muted-foreground">
                          {regionShapeMode === "rect" 
                            ? "Click and drag on the image to draw a rectangle region"
                            : "Click on the image to add polygon vertices"}
                        </div>
                      </div>
                    )}
                    
                    {/* Polygon drawing controls (Phase 4) */}
                    {regionDrawMode === "drawing_polygon" && (
                      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-card/95 px-3 py-2 rounded-lg shadow-lg border">
                        <span className="text-xs text-muted-foreground">
                          {polygonVertices.length} vertex{polygonVertices.length !== 1 ? 'es' : ''}
                          {polygonVertices.length < 3 && <span className="text-amber-600 ml-1">(min 3)</span>}
                        </span>
                        <Button
                          size="sm"
                          onClick={handleCompletePolygon}
                          disabled={polygonVertices.length < 3}
                          className="h-6 text-xs"
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Complete
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelPolygonDrawing}
                          className="h-6 text-xs"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Region List */}
                  {(question.heatMapConfig?.regions?.length || 0) > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Defined Regions</Label>
                      <div className="space-y-1">
                        {(question.heatMapConfig?.regions || []).map((region) => (
                          <div 
                            key={region.id} 
                            className={cn(
                              "flex items-center gap-2 p-2 rounded-md transition-colors cursor-pointer",
                              activeRegionId === region.id ? "bg-muted" : "hover:bg-muted/50"
                            )}
                            onClick={() => setActiveRegionId(region.id)}
                          >
                            {editingRegionName === region.id ? (
                              <Input
                                autoFocus
                                value={region.name}
                                onChange={(e) => handleRegionNameChange(region.id, e.target.value)}
                                onBlur={() => setEditingRegionName(null)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') setEditingRegionName(null);
                                }}
                                className="h-7 text-sm flex-1"
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span 
                                className="text-sm flex-1"
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  setEditingRegionName(region.id);
                                }}
                              >
                                {region.name}
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRegion(region.id);
                              }}
                              className="h-7 w-7"
                              aria-label={`Delete region ${region.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Region Info Note */}
                  <div className="flex items-start gap-2 bg-amber-50 p-2 rounded-lg">
                    <Target className="w-3 h-3 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-[11px] text-amber-700">
                      <span className="font-medium">Regions are for reporting only.</span> By default, respondents won't see them. 
                      Clicks inside a region will be labeled in exports for analysis.
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Note */}
              <div className="flex items-start gap-2 bg-fuchsia-50 p-3 rounded-lg">
                <MousePointerClick className="w-4 h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-fuchsia-700">
                  <span className="font-medium">Interaction:</span> Respondents can click up to {question.heatMapConfig?.maxClicks ?? 1} time{(question.heatMapConfig?.maxClicks ?? 1) > 1 ? 's' : ''} on the image. 
                  {(question.heatMapConfig?.maxClicks ?? 1) > 1 && question.heatMapConfig?.replaceOldest && " When the limit is reached, new clicks replace the oldest."}
                  {question.heatMapConfig?.allowDeselect && " Clicks can be removed by clicking on the dot."}
                </div>
              </div>
              
              {/* Region Import Dialog (Phase 4) */}
              <Dialog open={showRegionImportDialog} onOpenChange={setShowRegionImportDialog}>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Import Regions</DialogTitle>
                    <DialogDescription>
                      Paste region definitions in JSON format. Regions will be added to the existing ones.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <Textarea
                      value={regionImportJson}
                      onChange={(e) => setRegionImportJson(e.target.value)}
                      placeholder={`{
  "regions": [
    { "name": "Region 1", "shape": "rect", "xPct": 10, "yPct": 10, "wPct": 30, "hPct": 30 },
    { "name": "Region 2", "shape": "poly", "pointsPct": [
      { "xPct": 50, "yPct": 10 },
      { "xPct": 90, "yPct": 50 },
      { "xPct": 50, "yPct": 90 }
    ]}
  ]
}`}
                      className="h-64 font-mono text-xs"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowRegionImportDialog(false);
                        setRegionImportJson("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleImportRegions}
                      disabled={!regionImportJson.trim()}
                    >
                      Import
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            )}
          </div>
        );

      case "pick_group_rank":
        return (
          <div className="ml-11 space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Respondents will drag items into groups and optionally rank them:
            </p>
            <div className="bg-muted p-4 rounded-lg border space-y-4">
              {/* Items Configuration */}
              <div className="border-b pb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">Items (Drag Pool)</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const items = [...(question.pickGroupRankConfig?.items || [])];
                      const newItem: PickGroupRankItem = {
                        id: nanoid(10),
                        label: `Item ${items.length + 1}`,
                      };
                      items.push(newItem);
                      onChange({
                        ...question,
                        pickGroupRankConfig: {
                          ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                          ...question.pickGroupRankConfig,
                          items,
                        },
                      });
                    }}
                    className="h-7 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {(question.pickGroupRankConfig?.items || []).map((item, idx) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <span className="w-6 text-xs text-muted-foreground">{idx + 1}.</span>
                      <Input
                        value={item.label}
                        onChange={(e) => {
                          const items = [...(question.pickGroupRankConfig?.items || [])];
                          items[idx] = { ...item, label: e.target.value };
                          onChange({
                            ...question,
                            pickGroupRankConfig: {
                              ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                              ...question.pickGroupRankConfig,
                              items,
                            },
                          });
                        }}
                        placeholder={`Item ${idx + 1}`}
                        className="flex-1 h-8 text-sm"
                      />
                      {(question.pickGroupRankConfig?.items?.length || 0) > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const items = (question.pickGroupRankConfig?.items || []).filter((_, i) => i !== idx);
                            onChange({
                              ...question,
                              pickGroupRankConfig: {
                                ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                                ...question.pickGroupRankConfig,
                                items,
                              },
                            });
                          }}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Groups Configuration */}
              <div className="border-b pb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">Groups (Target Buckets)</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const groups = [...(question.pickGroupRankConfig?.groups || [])];
                      const newGroup: PickGroupRankGroup = {
                        id: nanoid(10),
                        label: `Group ${groups.length + 1}`,
                      };
                      groups.push(newGroup);
                      onChange({
                        ...question,
                        pickGroupRankConfig: {
                          ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                          ...question.pickGroupRankConfig,
                          groups,
                        },
                      });
                    }}
                    className="h-7 text-xs"
                    disabled={(question.pickGroupRankConfig?.groups?.length || 0) >= 10}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Group
                  </Button>
                </div>
                <div className="space-y-2">
                  {(question.pickGroupRankConfig?.groups || []).map((group, idx) => (
                    <div key={group.id} className="flex items-center gap-2">
                      <span className="w-6 text-xs text-muted-foreground">{idx + 1}.</span>
                      <Input
                        value={group.label}
                        onChange={(e) => {
                          const groups = [...(question.pickGroupRankConfig?.groups || [])];
                          groups[idx] = { ...group, label: e.target.value };
                          onChange({
                            ...question,
                            pickGroupRankConfig: {
                              ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                              ...question.pickGroupRankConfig,
                              groups,
                            },
                          });
                        }}
                        placeholder={`Group ${idx + 1}`}
                        className="flex-1 h-8 text-sm"
                      />
                      {(question.pickGroupRankConfig?.groups?.length || 0) > 2 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const groups = (question.pickGroupRankConfig?.groups || []).filter((_, i) => i !== idx);
                            onChange({
                              ...question,
                              pickGroupRankConfig: {
                                ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                                ...question.pickGroupRankConfig,
                                groups,
                              },
                            });
                          }}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Options Configuration */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Options</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`pgr-ranking-${question.id}`}
                      checked={question.pickGroupRankConfig?.allowRankingWithinGroups ?? false}
                      onCheckedChange={(checked) => {
                        onChange({
                          ...question,
                          pickGroupRankConfig: {
                            ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                            ...question.pickGroupRankConfig,
                            allowRankingWithinGroups: checked as boolean,
                          },
                        });
                      }}
                    />
                    <Label htmlFor={`pgr-ranking-${question.id}`} className="text-xs text-foreground cursor-pointer">
                      Allow ranking within groups
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`pgr-require-all-${question.id}`}
                      checked={question.pickGroupRankConfig?.requireAllItemsSorted ?? false}
                      onCheckedChange={(checked) => {
                        onChange({
                          ...question,
                          pickGroupRankConfig: {
                            ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                            ...question.pickGroupRankConfig,
                            requireAllItemsSorted: checked as boolean,
                          },
                        });
                      }}
                    />
                    <Label htmlFor={`pgr-require-all-${question.id}`} className="text-xs text-foreground cursor-pointer">
                      Require all items to be sorted
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`pgr-show-pool-${question.id}`}
                      checked={question.pickGroupRankConfig?.showItemPool ?? true}
                      onCheckedChange={(checked) => {
                        onChange({
                          ...question,
                          pickGroupRankConfig: {
                            ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                            ...question.pickGroupRankConfig,
                            showItemPool: checked as boolean,
                          },
                        });
                      }}
                    />
                    <Label htmlFor={`pgr-show-pool-${question.id}`} className="text-xs text-foreground cursor-pointer">
                      Show item pool
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`pgr-stack-${question.id}`}
                      checked={question.pickGroupRankConfig?.stackItemsInGroups ?? false}
                      onCheckedChange={(checked) => {
                        onChange({
                          ...question,
                          pickGroupRankConfig: {
                            ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                            ...question.pickGroupRankConfig,
                            stackItemsInGroups: checked as boolean,
                          },
                        });
                      }}
                    />
                    <Label htmlFor={`pgr-stack-${question.id}`} className="text-xs text-foreground cursor-pointer">
                      Stack items in groups (compact)
                    </Label>
                  </div>
                </div>

                {/* Display Mode */}
                <div className="flex items-center space-x-2">
                  <Label className="text-xs text-foreground">Layout:</Label>
                  <Select
                    value={question.pickGroupRankConfig?.displayMode || "columns"}
                    onValueChange={(value: "columns" | "stacked") => {
                      onChange({
                        ...question,
                        pickGroupRankConfig: {
                          ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                          ...question.pickGroupRankConfig,
                          displayMode: value,
                        },
                      });
                    }}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="columns">Columns</SelectItem>
                      <SelectItem value="stacked">Stacked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Validation Rules - Collapsible */}
              <Collapsible className="border-t pt-4">
                <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium hover:bg-muted rounded px-2 py-1 -mx-2">
                  <span>Validation Rules (Optional)</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Min per Group</Label>
                      <Input
                        type="number"
                        min={0}
                        value={question.pickGroupRankConfig?.validation?.minPerGroup ?? ""}
                        onChange={(e) => {
                          const val = e.target.value === "" ? undefined : parseInt(e.target.value);
                          onChange({
                            ...question,
                            pickGroupRankConfig: {
                              ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                              ...question.pickGroupRankConfig,
                              validation: {
                                ...question.pickGroupRankConfig?.validation,
                                minPerGroup: val,
                              },
                            },
                          });
                        }}
                        placeholder="0"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Max per Group</Label>
                      <Input
                        type="number"
                        min={1}
                        value={question.pickGroupRankConfig?.validation?.maxPerGroup ?? ""}
                        onChange={(e) => {
                          const val = e.target.value === "" ? undefined : parseInt(e.target.value);
                          onChange({
                            ...question,
                            pickGroupRankConfig: {
                              ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                              ...question.pickGroupRankConfig,
                              validation: {
                                ...question.pickGroupRankConfig?.validation,
                                maxPerGroup: val,
                              },
                            },
                          });
                        }}
                        placeholder="No limit"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Min Total Sorted</Label>
                      <Input
                        type="number"
                        min={0}
                        value={question.pickGroupRankConfig?.validation?.minTotalSorted ?? ""}
                        onChange={(e) => {
                          const val = e.target.value === "" ? undefined : parseInt(e.target.value);
                          onChange({
                            ...question,
                            pickGroupRankConfig: {
                              ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                              ...question.pickGroupRankConfig,
                              validation: {
                                ...question.pickGroupRankConfig?.validation,
                                minTotalSorted: val,
                              },
                            },
                          });
                        }}
                        placeholder="0"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Max Rank Columns (Export)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        value={question.pickGroupRankConfig?.maxRankColumnsExport ?? 10}
                        onChange={(e) => {
                          const val = e.target.value === "" ? 10 : Math.min(50, Math.max(1, parseInt(e.target.value)));
                          onChange({
                            ...question,
                            pickGroupRankConfig: {
                              ...DEFAULT_PICK_GROUP_RANK_CONFIG,
                              ...question.pickGroupRankConfig,
                              maxRankColumnsExport: val,
                            },
                          });
                        }}
                        placeholder="10"
                        className="h-8 text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Caps rank columns in CSV export</p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Preview Note */}
              <div className="flex items-start gap-2 bg-amber-50 p-3 rounded-lg">
                <GripVertical className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700">
                  <span className="font-medium">How it works:</span> Respondents drag items from the pool into groups. 
                  {question.pickGroupRankConfig?.allowRankingWithinGroups && " The order within each group represents their ranking."}
                  {!question.pickGroupRankConfig?.allowRankingWithinGroups && " Items within groups are not ranked."}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card 
      className={`group relative border-l-4 border-l-transparent hover:border-l-primary hover:shadow-md transition-all duration-200 ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary/20' : ''}`}
    >
      {/* Header Row */}
      <div className="flex items-start gap-3 p-4 pb-2">
        {/* Drag Handle */}
        <button
          type="button"
          className="mt-1 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted touch-none opacity-50 group-hover:opacity-100 transition-opacity"
          aria-label="Drag to reorder question"
          {...dragHandleProps}
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </button>
        
        {/* Question Number */}
        <Badge variant="outline" className="shrink-0 font-mono text-xs">
          {question.originalQuestionNumber || `Q${index + 1}`}
        </Badge>
        
        {/* Question Type Badge */}
        <Badge className={`shrink-0 ${getQuestionTypeColor(question.type)}`}>
          {getQuestionTypeLabel(question.type)}
        </Badge>
        
        {/* Required Indicator */}
        {question.required && (
          <Badge variant="secondary" className="shrink-0">
            Required
          </Badge>
        )}
        
        {/* Logic Indicator */}
        {!!(question.skipLogic?.length || question.optionLogic?.some(ol => ol.skipLogic?.action) || question.displayConditions?.length) && (
          <Badge variant="outline" className="shrink-0 border-purple-300 text-purple-600 bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:bg-purple-900/20">
            <GitBranch className="h-3 w-3 mr-1" />
            Logic
          </Badge>
        )}
        
        {/* Quota Check Indicator */}
        {question.segmentationRules?.some(r => r.quotaMax || r.quotaMin) && (
          <Badge variant="outline" className="shrink-0 border-orange-300 text-orange-600 bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:bg-orange-900/20">
            <Target className="h-3 w-3 mr-1" />
            Quota
          </Badge>
        )}
        
        {/* Spacer */}
        <div className="flex-1" />
        
        {/* Action Buttons */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            onClick={onDuplicate}
            aria-label="Duplicate question"
            className="h-8 w-8"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsEditing(!isEditing)}
            aria-label={isEditing ? "Finish editing" : "Edit question"}
            className="h-8 w-8"
          >
            {isEditing ? <Check className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            aria-label="Delete question"
            className="h-8 w-8 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <CardContent className="pt-0 px-4 pb-4">

        {/* Question Text - View/Edit Mode */}
        {isEditing ? (
          <Textarea
            value={question.question}
            onChange={(e) => handleQuestionTextChange(e.target.value)}
            placeholder="Enter your question"
            className="min-h-[80px] text-base font-medium"
          />
        ) : (
          <p className="text-base font-medium mb-4">{question.question}</p>
        )}

        {/* Question Type and Settings - Editing Mode */}
        {isEditing && (
          <div className="mt-4 space-y-4">
            {/* Required Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor={`required-${question.id}`} className="text-sm font-medium">
                  Required Question
                </Label>
                <p className="text-xs text-muted-foreground">
                  Respondents must answer this question
                </p>
              </div>
              <Switch
                id={`required-${question.id}`}
                checked={question.required ?? false}
                onCheckedChange={handleRequiredChange}
              />
            </div>
            
            {/* Question Type Selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Question Type</Label>
              <Select value={question.type} onValueChange={handleTypeChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                  <SelectItem value="single_choice">Single Choice</SelectItem>
                  <SelectItem value="open_text">Open Text</SelectItem>
                  <SelectItem value="likert_scale">Likert Scale</SelectItem>
                  <SelectItem value="dropdown">Dropdown</SelectItem>
                  <SelectItem value="ranking">Ranking</SelectItem>
                  <SelectItem value="rating">Rating</SelectItem>
                  <SelectItem value="radio_grid">Radio Grid (Matrix)</SelectItem>
                  <SelectItem value="checkbox_grid">Checkbox Grid (Matrix)</SelectItem>
                  <SelectItem value="dropdown_matrix">Dropdown Matrix</SelectItem>
                  <SelectItem value="text_matrix">Text Matrix</SelectItem>
                  <SelectItem value="numeric_matrix">Numeric Matrix</SelectItem>
                  <SelectItem value="ranking_matrix">Ranking Matrix</SelectItem>
                  <SelectItem value="date_time">Date/Time</SelectItem>
                  <SelectItem value="file_upload">File Upload</SelectItem>
                  <SelectItem value="slider_range">Slider Range</SelectItem>
                  <SelectItem value="video_response">Video Response</SelectItem>
                  <SelectItem value="image_choice">Image Choice</SelectItem>
                  <SelectItem value="heat_map">Heat Map</SelectItem>
                  <SelectItem value="pick_group_rank">Pick, Group & Rank</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Question Preview */}
        {renderQuestionPreview()}

        {/* Advanced Sections - Each in its own Collapsible (Phase 3.3) */}
        {isEditing && allQuestions && (
          <div className="mt-6 border-t pt-4 space-y-2">
            {/* Skip Logic */}
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2 w-full">
                <GitBranch className="h-4 w-4" />
                <span>Skip Logic</span>
                {(question.skipLogic?.length || 0) > 0 && (
                  <Badge variant="secondary" className="ml-auto">
                    {question.skipLogic?.length} rule{question.skipLogic?.length !== 1 ? 's' : ''}
                  </Badge>
                )}
                <ChevronDown className="h-4 w-4 ml-auto transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 pb-4">
                <LogicBuilder
                  question={question}
                  allQuestions={allQuestions}
                  onChange={(skipLogic: SkipLogicRule[]) => {
                    onChange({ ...question, skipLogic });
                  }}
                />
              </CollapsibleContent>
            </Collapsible>

            {/* Option Logic - only for choice-based questions */}
            {(question.type === "multiple_choice" || 
              question.type === "single_choice" || 
              question.type === "dropdown" ||
              question.type === "ranking") && 
              question.options && 
              question.options.length > 0 && (
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2 w-full">
                  <Settings2 className="h-4 w-4" />
                  <span>Option Logic</span>
                  {(() => {
                    const optionLogicWithAction = question.optionLogic?.filter(ol => ol.skipLogic?.action).length || 0;
                    return optionLogicWithAction > 0 && (
                      <Badge variant="secondary" className="ml-auto">
                        {optionLogicWithAction} option{optionLogicWithAction !== 1 ? 's' : ''}
                      </Badge>
                    );
                  })()}
                  <ChevronDown className="h-4 w-4 ml-auto transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 pb-4">
                  <OptionLogicEditor
                    question={question}
                    allQuestions={allQuestions}
                    onChange={(optionLogic: OptionLogic[]) => {
                      onChange({ ...question, optionLogic });
                    }}
                    onRandomizeOptionsChange={(randomize: boolean) => {
                      onChange({ ...question, randomizeOptions: randomize });
                    }}
                  />
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Display Conditions */}
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2 w-full">
                <Eye className="h-4 w-4" />
                <span>Display Conditions</span>
                {(question.displayConditions?.length || 0) > 0 && (
                  <Badge variant="secondary" className="ml-auto">
                    {question.displayConditions?.length} condition{question.displayConditions?.length !== 1 ? 's' : ''}
                  </Badge>
                )}
                <ChevronDown className="h-4 w-4 ml-auto transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 pb-4">
                <DisplayConditionBuilder
                  question={question}
                  allQuestions={allQuestions}
                  onChange={(displayConditions: DisplayCondition[]) => {
                    onChange({ ...question, displayConditions });
                  }}
                />
              </CollapsibleContent>
            </Collapsible>

            {/* Segmentation Rules */}
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2 w-full">
                <Users className="h-4 w-4" />
                <span>Segmentation Rules</span>
                {(question.segmentationRules?.length || 0) > 0 && (
                  <Badge variant="secondary" className="ml-auto">
                    {question.segmentationRules?.length} rule{question.segmentationRules?.length !== 1 ? 's' : ''}
                  </Badge>
                )}
                <ChevronDown className="h-4 w-4 ml-auto transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 pb-4">
                <SegmentationRuleEditor
                  question={question}
                  allQuestions={allQuestions}
                  onChange={(rules: SegmentationRule[]) => {
                    onChange({ ...question, segmentationRules: rules });
                  }}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
